import { sanitizeRichTextHtml } from '../utils/sanitizeHtml';
// src/AITestingTab.tsx

import React, { useState, useEffect, useMemo, useRef  } from 'react'
import {
  Card,
  Input,
  Select,
  Button,
  notification,
  Typography,
  Tag,
  Tabs,
  Table,
  Tooltip,
  Modal,
  List,
  Space,
  Popconfirm,
  Divider,
  Row,
  Col,
  Statistic,
  Progress,
  Rate,
  Alert,
  Form,
  Spin,
  InputNumber,
  Badge,
  Collapse,
  Empty
} from 'antd'

import { Checkbox } from 'antd'
import type { GetProps } from 'antd'

import {
  FolderOpenOutlined,
  DeleteOutlined,
  BarChartOutlined,
  EyeOutlined,
  SwapOutlined,
  InfoCircleOutlined,
  DownloadOutlined,
  ReloadOutlined,
  EditOutlined,
  PlusOutlined,
  TrophyOutlined,
  CrownOutlined,
} from '@ant-design/icons'

import ResultsTabs from './AITesting/ResultsTabs'
import CostQualityAnalysis from './AITesting/CostQualityAnalysis'
import SavedResultsModal from './AITesting/SavedResultsModal'
import IndividualRunsModal from './AITesting/IndividualRunsModal'
import TranslationModal from './AITesting/TranslationModal'
import SideBySideModal from './AITesting/SideBySideModal'
import CompareModal from './AITesting/CompareModal'
import AggregateResultsModal from './AITesting/AggregateResultsModal'
import AIExperimentTestingTab from './AIExperimentTestingTab'

// New shared modules
import {
  AggregatedScore,
  CompareResponse,
  EvaluationResult,
  ModelRanking,
  ModelResult,
  MultiRunAggregatedResult,
  TestInstruction,
  TestResultDB,
  TestText,
  TokenInfo,
  TranslationResult,
  IndividualRunDetail,
  TestErrorEntry,
} from './AITesting/types'

import { EVAL_WEIGHTS } from './AITesting/constants'

import {
  calculateCost,
  calculateModelCost,
  getAggregatedResults as getAggregatedResultsFromUtils,
  getModelRankings as getModelRankingsFromUtils,
  getScoreColor,
  hasAnyZeroScore,
  isValidScore,
  parseEvaluationResult,
} from './AITesting/utils'



import {
  compareTranslations,
  fetchAiTestingModelCatalog,
  AITestingModelCatalogEntry,
  deleteInstruction as apiDeleteInstruction,
  deleteSavedResult,
  deleteText as apiDeleteText,
  fetchDbResults as apiFetchDbResults,
  fetchDbResultsPaged as apiFetchDbResultsPaged,
  fetchDbResultsCount as apiFetchDbResultsCount,
  fetchAggregatedScores as apiFetchAggregatedScores,
  fetchAggregatedEvalTokens as apiFetchAggregatedEvalTokens,
  fetchAggregatedFilterOptions as apiFetchAggregatedFilterOptions,
  fetchInstructions as apiFetchInstructions,
  fetchTestTexts as apiFetchTestTexts,
  fetchProductionRules,
  runManualTest as apiRunManualTest,
  saveInstruction as apiSaveInstruction,
  saveText as apiSaveText,
  updateDiscardFlags,
  updateInstruction as apiUpdateInstruction,
  updateText as apiUpdateText,
  deleteResultsByLanguage,
} from './AITesting/api'

// Import distributed system components and API
import {
  runDistributedBatchTest,
  DistributedBatchRequest,
  DistributedBatchResponse
} from './AITesting/api_distributed'

import { 
  DistributedBatchProgress, 
  ActiveBatchesList 
} from './AITesting/DistributedBatchProgress'
import './AITestingTab.css'

type CheckboxGroupProps = GetProps<typeof Checkbox.Group>
type CheckboxValueType = NonNullable<CheckboxGroupProps['value']>[number]

type AITestingModelOption = {
  id: string
  label: string
  provider: string
  providerLabel: string
  apiModel: string
  inputCostPerMTokens?: number
  cachedInputCostPerMTokens?: number
  outputCostPerMTokens?: number
  isActive: boolean
  testingOnly: boolean
}

type ModelGroup = {
  key: string
  label: string
  models: AITestingModelOption[]
}

const DEFAULT_MODEL_SELECTION = [
  'Claude3.7',
  'DeepSeekR10528',
  'ChatGPT4.1',
  'Gemini2.5Pro',
  'Gemini3Pro',
  'GPT5-Medium'
]
export const DEFAULT_EVALUATION_SELECTION = ['Gemini3Pro', 'ChatGPT5.2', 'sonnet45']
const MAX_EVALUATORS = 4

export const isAllowedAItestingProvider = (provider?: string): boolean => {
  const normalized = (provider || '').trim().toLowerCase()
  if (!normalized) return false
  return normalized === 'openai' || normalized.includes('openrouter')
}

const getProviderLabel = (provider: string): string => {
  const normalized = (provider || '').trim().toLowerCase()
  if (normalized === 'openai') return 'OpenAI'
  if (normalized === 'generic_openrouter') return 'OpenRouter (Generic)'
  if (normalized.endsWith('_openrouter')) {
    const prefix = normalized.replace('_openrouter', '').replace(/_/g, ' ')
    return `OpenRouter (${prefix})`
  }
  return provider
}

export const toModelOption = (entry: AITestingModelCatalogEntry): AITestingModelOption | null => {
  const modelId = (entry.id || '').trim()
  const provider = (entry.provider || '').trim()
  if (!modelId || !provider || !isAllowedAItestingProvider(provider)) return null
  return {
    id: modelId,
    label: (entry.displayName || modelId).trim(),
    provider,
    providerLabel: getProviderLabel(provider),
    apiModel: (entry.apiModel || modelId).trim(),
    inputCostPerMTokens: entry.inputCostPerMTokens,
    cachedInputCostPerMTokens: entry.cachedInputCostPerMTokens,
    outputCostPerMTokens: entry.outputCostPerMTokens,
    isActive: Boolean(entry.isActive),
    testingOnly: Boolean(entry.testingOnly),
  }
}

export const buildFilteredModelGroups = (
  models: AITestingModelOption[],
  searchTerm: string
): ModelGroup[] => {
  const normalizedSearch = searchTerm.trim().toLowerCase()
  const grouped = models.reduce<Record<string, ModelGroup>>((acc, model) => {
    const matchesSearch =
      !normalizedSearch ||
      model.id.toLowerCase().includes(normalizedSearch) ||
      model.label.toLowerCase().includes(normalizedSearch)
    if (!matchesSearch) return acc
    if (!acc[model.provider]) {
      acc[model.provider] = {
        key: model.provider,
        label: model.providerLabel,
        models: [],
      }
    }
    acc[model.provider].models.push(model)
    return acc
  }, {})

  return Object.values(grouped)
    .map(group => ({
      ...group,
      models: [...group.models].sort((a, b) => a.id.localeCompare(b.id)),
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

export const getInitialEvaluationSelection = (
  previousSelection: string[],
  availableModelIds: Set<string>,
  modelCatalog: AITestingModelOption[]
): string[] => {
  const stillValid = previousSelection.filter(id => availableModelIds.has(id))
  if (stillValid.length > 0) return stillValid.slice(0, MAX_EVALUATORS)

  const defaults = DEFAULT_EVALUATION_SELECTION.filter(id => availableModelIds.has(id))
  if (defaults.length > 0) return defaults.slice(0, MAX_EVALUATORS)

  return modelCatalog.slice(0, 1).map(model => model.id)
}

export const enforceEvaluationSelectionLimit = (values: string[]): string[] | null => {
  if (values.length > MAX_EVALUATORS) return null
  return values
}

type AggregateInstructionOption = { id: string; name: string; count: number }
type AggregateTestNameOption = { name: string; count: number }
type AggregateBatchOption = { id: string; name: string; count: number; latestCreatedAt?: string }

export const buildInstructionOptionsFromResults = (
  results: Pick<TestResultDB, 'language' | 'instruction_id' | 'instruction_name' | 'test_name'>[],
  language: string,
  testName?: string
): AggregateInstructionOption[] => {
  if (!language) return []
  const normalizedTestName = (testName || '').trim()
  const languageResults = results.filter(
    r => r.language === language && (!normalizedTestName || (r.test_name || '').trim() === normalizedTestName)
  )
  const instructionGroups = languageResults.reduce((acc, result) => {
    const key = (result.instruction_id || '').trim()
    if (!key) return acc
    const name = (result.instruction_name || '').trim() || key
    if (!acc[key]) acc[key] = { id: key, name, count: 0 }
    acc[key].count += 1
    if (name && acc[key].name === key) {
      acc[key].name = name
    }
    return acc
  }, {} as Record<string, AggregateInstructionOption>)
  return Object.values(instructionGroups).sort((a, b) => a.name.localeCompare(b.name))
}

export const buildTestNameOptionsFromResults = (
  results: Pick<TestResultDB, 'language' | 'test_name' | 'instruction_id'>[],
  language: string,
  instructionId?: string
): AggregateTestNameOption[] => {
  if (!language) return []
  const normalizedInstructionId = (instructionId || '').trim()
  const languageResults = results.filter(
    r => r.language === language && (!normalizedInstructionId || (r.instruction_id || '').trim() === normalizedInstructionId)
  )
  const grouped = languageResults.reduce((acc, result) => {
    const name = (result.test_name || '').trim()
    if (!name) return acc
    if (!acc[name]) acc[name] = { name, count: 0 }
    acc[name].count += 1
    return acc
  }, {} as Record<string, AggregateTestNameOption>)
  return Object.values(grouped).sort((a, b) => a.name.localeCompare(b.name))
}

export const buildBatchOptionsFromResults = (
  results: Pick<TestResultDB, 'language' | 'batch_id' | 'test_name' | 'instruction_id' | 'created_at'>[],
  language: string,
  instructionId?: string,
  testName?: string
): AggregateBatchOption[] => {
  if (!language) return []
  const normalizedInstructionId = (instructionId || '').trim()
  const normalizedTestName = (testName || '').trim()
  const languageResults = results.filter((r) => {
    if (r.language !== language) return false
    if (normalizedInstructionId && (r.instruction_id || '').trim() !== normalizedInstructionId) return false
    if (normalizedTestName && (r.test_name || '').trim() !== normalizedTestName) return false
    return true
  })

  const grouped = languageResults.reduce((acc, result) => {
    const batchId = (result.batch_id || '').trim()
    if (!batchId) return acc
    if (!acc[batchId]) {
      acc[batchId] = {
        id: batchId,
        name: (result.test_name || '').trim() || `Batch ${batchId.slice(0, 8)}`,
        count: 0,
        latestCreatedAt: result.created_at,
      }
    }
    acc[batchId].count += 1
    const candidateTs = new Date(result.created_at || '').getTime()
    const currentTs = new Date(acc[batchId].latestCreatedAt || '').getTime()
    if (!Number.isNaN(candidateTs) && (Number.isNaN(currentTs) || candidateTs > currentTs)) {
      acc[batchId].latestCreatedAt = result.created_at
    }
    return acc
  }, {} as Record<string, AggregateBatchOption>)

  const options = Object.values(grouped)
  options.sort((a, b) => a.name.localeCompare(b.name))
  options.sort((a, b) => {
    const left = new Date(a.latestCreatedAt || '').getTime()
    const right = new Date(b.latestCreatedAt || '').getTime()
    if (Number.isNaN(left) && Number.isNaN(right)) return 0
    if (Number.isNaN(left)) return 1
    if (Number.isNaN(right)) return -1
    return right - left
  })
  return options
}

const buildAggregateFilterOptionsFromResults = (
  results: Pick<TestResultDB, 'language' | 'instruction_id' | 'instruction_name' | 'test_name' | 'batch_id' | 'created_at'>[],
  language: string,
  instructionId?: string,
  testName?: string,
  batchId?: string
): {
  instructions: AggregateInstructionOption[]
  testNames: AggregateTestNameOption[]
  batches: AggregateBatchOption[]
} => {
  const normalizedInstruction = (instructionId || '').trim()
  const normalizedTestName = (testName || '').trim()
  const normalizedBatchId = (batchId || '').trim()

  const languageRows = results.filter(r => r.language === language)

  const instructionRows = languageRows.filter(r => {
    if (normalizedBatchId && (r.batch_id || '').trim() !== normalizedBatchId) return false
    if (normalizedTestName && (r.test_name || '').trim() !== normalizedTestName) return false
    return true
  })

  const testNameRows = languageRows.filter(r => {
    if (normalizedBatchId && (r.batch_id || '').trim() !== normalizedBatchId) return false
    if (normalizedInstruction && (r.instruction_id || '').trim() !== normalizedInstruction) return false
    return true
  })

  const batchRows = languageRows.filter(r => {
    if (normalizedInstruction && (r.instruction_id || '').trim() !== normalizedInstruction) return false
    if (normalizedTestName && (r.test_name || '').trim() !== normalizedTestName) return false
    return true
  })

  return {
    instructions: buildInstructionOptionsFromResults(instructionRows, language),
    testNames: buildTestNameOptionsFromResults(testNameRows, language),
    batches: buildBatchOptionsFromResults(batchRows, language),
  }
}

const { Panel } = Collapse
const { TextArea } = Input
const { Title, Text } = Typography
const { Option } = Select

export const LegacyAITestingTab: React.FC = () => {
  const [instruction, setInstruction] = useState<string>('')
  const [importingProductionRules, setImportingProductionRules] = useState<boolean>(false)
  const [textToTranslate, setTextToTranslate] = useState<string>('')
  const [selectedModels, setSelectedModels] = useState<string[]>([])
  const [modelSearchTerm, setModelSearchTerm] = useState<string>('')
  const [selectedEvaluationModels, setSelectedEvaluationModels] = useState<string[]>([])
  const [evaluationModelSearchTerm, setEvaluationModelSearchTerm] = useState<string>('')
  const [loadingModelCatalog, setLoadingModelCatalog] = useState<boolean>(false)
  const [modelCatalog, setModelCatalog] = useState<AITestingModelOption[]>([])
  const [selectedLanguage, setSelectedLanguage] = useState<string>('English')
  const [enforceFrenchEmDash, setEnforceFrenchEmDash] = useState<boolean>(false)
  const [emDashInstructions, setEmDashInstructions] = useState<string>('')
  const [proofreaderEnabled, setProofreaderEnabled] = useState<boolean>(true)
  const [proofreaderFormalityModel, setProofreaderFormalityModel] = useState<string>('')
  const [proofreaderGeneralModel, setProofreaderGeneralModel] = useState<string>('')
  const [proofreaderAlignmentModel, setProofreaderAlignmentModel] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [translationResults, setTranslationResults] = useState<TranslationResult[]>([])
  const [evaluationResults, setEvaluationResults] = useState<ModelResult[]>([])
  const [showSavedModal, setShowSavedModal] = useState<boolean>(false)
  const [showAggregateModal, setShowAggregateModal] = useState<boolean>(false)
  const [testName, setTestName] = useState<string>('')
  const [selectedLanguageForAggregate, setSelectedLanguageForAggregate] = useState<string>('')
  const [selectedInstructionForAggregate, setSelectedInstructionForAggregate] = useState<string>('')
  const [selectedTestNameForAggregate, setSelectedTestNameForAggregate] = useState<string>('')
  const [selectedBatchForAggregate, setSelectedBatchForAggregate] = useState<string>('')
  const [aggregateInstructionOptions, setAggregateInstructionOptions] = useState<AggregateInstructionOption[]>([])
  const [aggregateTestNameOptions, setAggregateTestNameOptions] = useState<AggregateTestNameOption[]>([])
  const [aggregateBatchOptions, setAggregateBatchOptions] = useState<AggregateBatchOption[]>([])
  const [aggregateFiltersDirty, setAggregateFiltersDirty] = useState<boolean>(true)
  const [hasLoadedAggregateData, setHasLoadedAggregateData] = useState<boolean>(false)
  const [aggregateLoadRequest, setAggregateLoadRequest] = useState<{
    language: string
    instructionId?: string
    testName?: string
    batchId?: string
    requestId: number
  } | null>(null)
  const [showTranslationModal, setShowTranslationModal] = useState<boolean>(false)
  const [translationToShow, setTranslationToShow] = useState<string>('')
  const [selectedCompareModels, setSelectedCompareModels] = useState<string[]>([])
  const [showCompareModal, setShowCompareModal] = useState<boolean>(false)
  const [compareResult, setCompareResult] = useState<CompareResponse | null>(null)
  const [showSideBySideModal, setShowSideBySideModal] = useState<boolean>(false)
  const [sideBySideTranslation, setSideBySideTranslation] = useState<string>('')

  // Em-dash before/after compare state
  const [showEmDashCompareModal, setShowEmDashCompareModal] = useState<boolean>(false)
  const [emDashBeforeHTML, setEmDashBeforeHTML] = useState<string>('')
  const [emDashAfterHTML, setEmDashAfterHTML] = useState<string>('')
  const [instructions, setInstructions] = useState<TestInstruction[]>([])
  const [testTexts, setTestTexts] = useState<TestText[]>([])
  const [selectedTextIds, setSelectedTextIds] = useState<string[]>([])
  
  // Pagination for Saved Results
  const [savedTotalCount, setSavedTotalCount] = useState<number>(0)
  const [savedPage, setSavedPage] = useState<number>(1)
  const [savedPageSize, setSavedPageSize] = useState<number>(10)
  
  // Aggregates for AggregateResultsModal (server-side, ALL tests)
  const [aggregatedScores, setAggregatedScores] = useState<AggregatedScore[]>([])
  type TokenBreakdown = { inputTokens: number; cachedTokens: number; outputTokens: number; reasoningTokens: number }
  type CostBreakdown = { inputCost: number; cachedCost: number; outputCost: number; reasoningCost: number; totalCost: number }
  type EvalCostAggregate = {
    model: string
    // Translation tokens aggregated across tests for this translation model
    tokenBreakdown: TokenBreakdown
    // Evaluation cost totals (sum across evaluators)
    costBreakdown: CostBreakdown
    evaluators: Record<string, { tokenBreakdown: TokenBreakdown; costBreakdown: CostBreakdown }>
    testCount: number
    // Added: translation cost breakdown computed from tokenBreakdown and translation model rates
    translationCostBreakdown: CostBreakdown
  }
  const [aggregatedEvalCosts, setAggregatedEvalCosts] = useState<EvalCostAggregate[]>([])
  const [aggregateTotalCount, setAggregateTotalCount] = useState<number>(0)
  const [loadingAggregateData, setLoadingAggregateData] = useState<boolean>(false)
  const [loadingAggregateInstructionOptions, setLoadingAggregateInstructionOptions] = useState<boolean>(false)
  const [loadingAggregateTestNameOptions, setLoadingAggregateTestNameOptions] = useState<boolean>(false)
  const [loadingAggregateBatchOptions, setLoadingAggregateBatchOptions] = useState<boolean>(false)
  
  // Distributed batch processing states
  const [activeBatchId, setActiveBatchId] = useState<string>('')
  const [showBatchProgress, setShowBatchProgress] = useState<boolean>(false)
  const [showActiveBatches, setShowActiveBatches] = useState<boolean>(false)

  const availableModelIds = useMemo<Set<string>>(
    () => new Set(modelCatalog.map(m => m.id)),
    [modelCatalog]
  )
  type PricingRate = { input: number; cachedInput?: number; output: number }
  const modelApiById = useMemo<Record<string, string>>(() => {
    return modelCatalog.reduce<Record<string, string>>((acc, model) => {
      acc[model.id] = model.apiModel || model.id
      return acc
    }, {})
  }, [modelCatalog])
  const modelCostRates = useMemo<Record<string, PricingRate>>(() => {
    const rateMap: Record<string, PricingRate> = {}
    modelCatalog.forEach(model => {
      const input = Number(model.inputCostPerMTokens)
      const output = Number(model.outputCostPerMTokens)
      if (!Number.isFinite(input) || input < 0 || !Number.isFinite(output) || output < 0) {
        return
      }
      const cachedRaw = model.cachedInputCostPerMTokens
      const normalized: PricingRate = { input, output }
      if (cachedRaw !== undefined && cachedRaw !== null) {
        const cached = Number(cachedRaw)
        if (Number.isFinite(cached) && cached >= 0) {
          normalized.cachedInput = cached
        }
      }
      rateMap[model.id] = normalized
      const apiModel = (model.apiModel || '').trim()
      if (apiModel) {
        rateMap[apiModel] = normalized
      }
    })
    return rateMap
  }, [modelCatalog])

  const filteredModelGroups = useMemo<ModelGroup[]>(() => {
    return buildFilteredModelGroups(modelCatalog, modelSearchTerm)
  }, [modelCatalog, modelSearchTerm])

  const filteredEvaluationModelGroups = useMemo<ModelGroup[]>(() => {
    return buildFilteredModelGroups(modelCatalog, evaluationModelSearchTerm)
  }, [modelCatalog, evaluationModelSearchTerm])
  const trimmedModelSearch = modelSearchTerm.trim()
  const trimmedEvaluationModelSearch = evaluationModelSearchTerm.trim()
  const [distributedBatchResults, setDistributedBatchResults] = useState<any[]>([])
  const [selectedInstructionId, setSelectedInstructionId] = useState<string>('')
  const [numRuns, setNumRuns] = useState<number>(1)
  const [showInstructionModal, setShowInstructionModal] = useState<boolean>(false)
  const [showTextModal, setShowTextModal] = useState<boolean>(false)
  const [editingInstruction, setEditingInstruction] = useState<TestInstruction | null>(null)
  const [editingText, setEditingText] = useState<TestText | null>(null)
  const [loadingInstructions, setLoadingInstructions] = useState<boolean>(false)
  const [loadingTexts, setLoadingTexts] = useState<boolean>(false)
  const [dbResults, setDbResults] = useState<TestResultDB[]>([])
  const [loadingDbResults, setLoadingDbResults] = useState<boolean>(false)
  const [loadingDeleteAll, setLoadingDeleteAll] = useState<boolean>(false)
  const [multiRunResults, setMultiRunResults] = useState<MultiRunAggregatedResult | null>(null)
  const [loadingMultiRun, setLoadingMultiRun] = useState<boolean>(false)
  const [showIndividualRunsModal, setShowIndividualRunsModal] = useState<boolean>(false)
  const [currentResultId, setCurrentResultId] = useState<string>('')
  const [testErrors, setTestErrors] = useState<TestErrorEntry[]>([])
  const [deletedResults, setDeletedResults] = useState<Set<string>>(new Set())
  const [instructionForm] = Form.useForm()
  const [textForm] = Form.useForm()

  useEffect(() => {
    const loadModelCatalog = async (): Promise<void> => {
      setLoadingModelCatalog(true)
      try {
        const data = await fetchAiTestingModelCatalog(true)
        const filtered = data
          .map(toModelOption)
          .filter((model): model is AITestingModelOption => Boolean(model))
          .sort((a, b) => a.id.localeCompare(b.id))
        setModelCatalog(filtered)
      } catch (error: unknown) {
        const msg = error instanceof Error ? error.message : 'Unknown error'
        notification.error({ message: 'Failed to load AI model catalog', description: msg })
      } finally {
        setLoadingModelCatalog(false)
      }
    }
    loadModelCatalog()
  }, [])

  useEffect(() => {
    if (!modelCatalog.length) return
    setSelectedModels(prev => {
      const stillValid = prev.filter(id => availableModelIds.has(id))
      if (stillValid.length > 0) return stillValid
      const defaults = DEFAULT_MODEL_SELECTION.filter(id => availableModelIds.has(id))
      if (defaults.length > 0) return defaults
      return modelCatalog.slice(0, 3).map(model => model.id)
    })
    setSelectedEvaluationModels(prev => {
      return getInitialEvaluationSelection(prev, availableModelIds, modelCatalog)
    })
  }, [availableModelIds, modelCatalog])

  useEffect(() => {
    setProofreaderFormalityModel(prev => (prev && availableModelIds.has(prev) ? prev : ''))
    setProofreaderGeneralModel(prev => (prev && availableModelIds.has(prev) ? prev : ''))
    setProofreaderAlignmentModel(prev => (prev && availableModelIds.has(prev) ? prev : ''))
  }, [availableModelIds])

  const handleLoadSavedResult = async (item: TestResultDB): Promise<void> => {
    const loadedTranslationModels = (item.models || []).filter(modelId => availableModelIds.has(modelId))
    if (loadedTranslationModels.length > 0) {
      setSelectedModels(loadedTranslationModels)
    }
    const loadedEvaluationModels = (item.evaluation_models || []).filter(modelId => availableModelIds.has(modelId))
    if (loadedEvaluationModels.length > 0) {
      setSelectedEvaluationModels(loadedEvaluationModels.slice(0, 4))
    }

    const isMultiRun = item.total_runs > 1
    setCurrentResultId(item.id)
    if (isMultiRun) {
      setTestErrors([])
      await aggregateMultiRunResults(item.test_name, item.language)
      setShowSavedModal(false)
      notification.success({
        message: 'Aggregated Results Loaded',
        description: `Loaded averages from ${item.total_runs} runs.`,
      })
    } else {
      setTranslationResults(item.translation_results)
      setEvaluationResults(item.evaluation_results)
      setMultiRunResults(null)
      const combinedErrors: TestErrorEntry[] = [
        ...(item.translation_errors ?? []),
        ...(item.evaluation_errors ?? []),
      ]
      setTestErrors(combinedErrors)
      setDeletedResults(new Set(item.discarded_evaluations || []))
      setShowSavedModal(false)
      notification.success({
        message: 'Results Loaded',
        description: 'Test results have been loaded.',
      })
    }
  }

  const handleDeleteSavedResult = async (id: string): Promise<void> => {
    try {
      await deleteSavedResult(id)
      notification.success({ message: 'Result deleted' })
      fetchDbResults()
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Unknown error'
      notification.error({ message: 'Failed to delete result', description: msg })
    }
  }

  useEffect(() => {
    if (selectedLanguage) {
      fetchInstructions()
      fetchDbResults(1, savedPageSize)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage])

  useEffect(() => {
    // Auto-disable the em-dash enforcement option when not in French
    if (selectedLanguage !== 'French' && enforceFrenchEmDash) {
      setEnforceFrenchEmDash(false)
    }
  }, [selectedLanguage])

  // Load heavy aggregate score/token data only after user explicitly applies filters.
  useEffect(() => {
    let active = true
    const loadAggregates = async () => {
      if (!showAggregateModal || !aggregateLoadRequest || !aggregateLoadRequest.language) {
        setLoadingAggregateData(false)
        return
      }

      setLoadingAggregateData(true)
      try {
        const scores = await apiFetchAggregatedScores(
          aggregateLoadRequest.language,
          aggregateLoadRequest.instructionId,
          aggregateLoadRequest.testName,
          aggregateLoadRequest.batchId
        )
        if (!active) return
        setAggregatedScores(scores || [])

        const tokenAggs = await apiFetchAggregatedEvalTokens(
          aggregateLoadRequest.language,
          aggregateLoadRequest.instructionId,
          aggregateLoadRequest.testName,
          aggregateLoadRequest.batchId
        )
        if (!active) return

        // Convert tokens to costs by evaluator and by model totals
        const toCost = (tb: TokenBreakdown, priceKey: string): CostBreakdown => {
          const rates = modelCostRates[priceKey]
          const inputRate = rates?.input ?? 0
          const outputRate = rates?.output ?? 0
          const cachedRate = rates?.cachedInput != null ? rates.cachedInput : inputRate * 0.1
          const inputCost = (tb.inputTokens / 1_000_000) * inputRate
          const cachedCost = (tb.cachedTokens / 1_000_000) * cachedRate
          const outputCost = (tb.outputTokens / 1_000_000) * outputRate
          const reasoningCost = (tb.reasoningTokens / 1_000_000) * outputRate
          const totalCost = inputCost + cachedCost + outputCost + reasoningCost
          return { inputCost, cachedCost, outputCost, reasoningCost, totalCost }
        }

        const processed: EvalCostAggregate[] = (tokenAggs || []).map((m: any) => {
          const evaluators: Record<string, { tokenBreakdown: TokenBreakdown; costBreakdown: CostBreakdown }> = {}
          const modelCost: CostBreakdown = { inputCost: 0, cachedCost: 0, outputCost: 0, reasoningCost: 0, totalCost: 0 }
          Object.entries(m.evaluators || {}).forEach(([evalModel, data]: any) => {
            const tb: TokenBreakdown = {
              inputTokens: Math.max(0, Number(data?.tokenBreakdown?.inputTokens) || 0),
              cachedTokens: Math.max(0, Number(data?.tokenBreakdown?.cachedTokens) || 0),
              outputTokens: Math.max(0, Number(data?.tokenBreakdown?.outputTokens) || 0),
              reasoningTokens: Math.max(0, Number(data?.tokenBreakdown?.reasoningTokens) || 0),
            }
            const priceKey = evalModel
            const cb = toCost(tb, priceKey)
            evaluators[evalModel] = { tokenBreakdown: tb, costBreakdown: cb }
            modelCost.inputCost += cb.inputCost
            modelCost.cachedCost += cb.cachedCost
            modelCost.outputCost += cb.outputCost
            modelCost.reasoningCost += cb.reasoningCost
            modelCost.totalCost += cb.totalCost
          })
          const modelTokens: TokenBreakdown = {
            inputTokens: Math.max(0, Number(m?.tokenBreakdown?.inputTokens) || 0),
            cachedTokens: Math.max(0, Number(m?.tokenBreakdown?.cachedTokens) || 0),
            outputTokens: Math.max(0, Number(m?.tokenBreakdown?.outputTokens) || 0),
            reasoningTokens: Math.max(0, Number(m?.tokenBreakdown?.reasoningTokens) || 0),
          }
          const transPriceKey = m.model
          const transCost = toCost(modelTokens, transPriceKey)
          return {
            model: m.model,
            tokenBreakdown: modelTokens,
            costBreakdown: modelCost,
            evaluators,
            testCount: Number(m?.testCount) || 0,
            translationCostBreakdown: transCost,
          }
        })
        setAggregatedEvalCosts(processed)
        setHasLoadedAggregateData(true)
      } catch (e) {
        console.error('Failed to load aggregates', e)
        if (!active) return
        setAggregatedScores([])
        setAggregatedEvalCosts([])
        setHasLoadedAggregateData(false)
      } finally {
        if (active) {
          setLoadingAggregateData(false)
        }
      }
    }
    loadAggregates()
    return () => {
      active = false
    }
  }, [showAggregateModal, aggregateLoadRequest, modelCostRates])

  // Fetch total count for selected language in Aggregate modal
  useEffect(() => {
    const loadAggregateCount = async () => {
      if (!showAggregateModal || !selectedLanguageForAggregate) return
      try {
        const total = await apiFetchDbResultsCount(selectedLanguageForAggregate)
        setAggregateTotalCount(total)
      } catch (e) {
        // ignore; keep previous value
      }
    }
    loadAggregateCount()
  }, [showAggregateModal, selectedLanguageForAggregate])

  // Fetch aggregate filter options in a single request (instructions/test names/batches).
  useEffect(() => {
    let active = true
    const loadAggregateFilterOptions = async () => {
      if (!showAggregateModal || !selectedLanguageForAggregate) {
        setLoadingAggregateInstructionOptions(false)
        setLoadingAggregateTestNameOptions(false)
        setLoadingAggregateBatchOptions(false)
        setAggregateInstructionOptions([])
        setAggregateTestNameOptions([])
        setAggregateBatchOptions([])
        return
      }

      setLoadingAggregateInstructionOptions(true)
      setLoadingAggregateTestNameOptions(true)
      setLoadingAggregateBatchOptions(true)

      try {
        const payload = await apiFetchAggregatedFilterOptions(
          selectedLanguageForAggregate,
          selectedInstructionForAggregate || undefined,
          selectedTestNameForAggregate || undefined,
          selectedBatchForAggregate || undefined
        )
        if (!active) return

        const instructions = payload?.instructions || []
        const testNames = payload?.testNames || []
        const batches = payload?.batches || []

        setAggregateInstructionOptions(instructions)
        setAggregateTestNameOptions(testNames)
        setAggregateBatchOptions(batches)

        setSelectedInstructionForAggregate(prev =>
          prev && !instructions.some(option => option.id === prev) ? '' : prev
        )
        setSelectedTestNameForAggregate(prev =>
          prev && !testNames.some(option => option.name === prev) ? '' : prev
        )
        setSelectedBatchForAggregate(prev =>
          prev && !batches.some(option => option.id === prev) ? '' : prev
        )
      } catch (error) {
        console.error('Failed to load aggregate filter options', error)
        if (!active) return

        const fallback = buildAggregateFilterOptionsFromResults(
          dbResults,
          selectedLanguageForAggregate,
          selectedInstructionForAggregate || undefined,
          selectedTestNameForAggregate || undefined,
          selectedBatchForAggregate || undefined
        )
        setAggregateInstructionOptions(fallback.instructions)
        setAggregateTestNameOptions(fallback.testNames)
        setAggregateBatchOptions(fallback.batches)
      } finally {
        if (active) {
          setLoadingAggregateInstructionOptions(false)
          setLoadingAggregateTestNameOptions(false)
          setLoadingAggregateBatchOptions(false)
        }
      }
    }

    loadAggregateFilterOptions()
    return () => {
      active = false
    }
  }, [
    showAggregateModal,
    selectedLanguageForAggregate,
    selectedInstructionForAggregate,
    selectedTestNameForAggregate,
    selectedBatchForAggregate,
    dbResults,
  ])

  useEffect(() => {
    fetchTestTexts()
  }, [])

  useEffect(() => {
    if (!currentResultId) {
      if (evaluationResults.length > 0 && dbResults.length > 0) return
      setDeletedResults(new Set())
      return
    }
    const rec = dbResults.find(r => r.id === currentResultId)
    setDeletedResults(new Set(rec?.discarded_evaluations || []))
  }, [currentResultId, dbResults, evaluationResults])

  const deleteAllTestsForLanguage = async (): Promise<void> => {
    if (!selectedLanguage) {
      notification.warning({
        message: 'No Language Selected',
        description: 'Please select a language first.',
      })
      return
    }
    setLoadingDeleteAll(true)
    try {
      // Use backend bulk delete to remove ALL tests for the language
      const res = await deleteResultsByLanguage(selectedLanguage)
      notification.success({
        message: 'Deletion Complete',
        description: `Deleted ${res.deleted}/${res.matched} tests for ${selectedLanguage}.`,
      })
      await fetchDbResults(1, savedPageSize)
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Unknown error'
      notification.error({ message: 'Deletion Failed', description: msg })
    } finally {
      setLoadingDeleteAll(false)
    }
  }

  const fetchInstructions = async (): Promise<void> => {
    setLoadingInstructions(true)
    try {
      const data = await apiFetchInstructions(selectedLanguage)
      setInstructions(data)
      if (data.length === 1) {
        setSelectedInstructionId(data[0].id)
      } else {
        setSelectedInstructionId('')
      }
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Unknown error'
      notification.error({ message: 'Failed to load instructions', description: msg })
    } finally {
      setLoadingInstructions(false)
    }
  }

  const toggleResultDeletion = async (modelKey: string): Promise<void> => {
    if (!currentResultId) {
      notification.warning({
        message: 'No Test Loaded',
        description: 'Please load a test result first.',
      })
      return
    }
    const isDeleted = deletedResults.has(modelKey)
    const newDeletedSet = new Set(deletedResults)
    if (isDeleted) newDeletedSet.delete(modelKey)
    else newDeletedSet.add(modelKey)
    setDeletedResults(newDeletedSet)
    const updatedList = Array.from(newDeletedSet)
    try {
      await updateDiscardFlags(currentResultId, updatedList)
      setDbResults(prev => prev.map(r => r.id === currentResultId ? { ...r, discarded_evaluations: updatedList } : r))
      notification.success({
        message: 'Updated Discard Flags',
        description: `${modelKey} has been ${isDeleted ? 'restored' : 'discarded'}.`,
      })
    } catch (error: unknown) {
      setDeletedResults(deletedResults)
      const msg = error instanceof Error ? error.message : 'Unknown error'
      notification.error({ message: 'Update Failed', description: msg })
    }
  }

  const fetchTestTexts = async (): Promise<void> => {
    setLoadingTexts(true)
    try {
      const data = await apiFetchTestTexts()
      setTestTexts(data)
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Unknown error'
      notification.error({ message: 'Failed to load test texts', description: msg })
    } finally {
      setLoadingTexts(false)
    }
  }

  const fetchDbResults = async (page?: number, pageSize?: number): Promise<void> => {
    if (!selectedLanguage) return
    const pg = page ?? savedPage
    const ps = pageSize ?? savedPageSize
    const offset = (pg - 1) * ps
    setLoadingDbResults(true)
    try {
      const [data, total] = await Promise.all([
        apiFetchDbResultsPaged(selectedLanguage, ps, offset),
        apiFetchDbResultsCount(selectedLanguage),
      ])
      // Always show newest first in the "View Saved" modal
      const sorted = [...data].sort((a, b) => {
        const tb = new Date(b.created_at).getTime() || 0
        const ta = new Date(a.created_at).getTime() || 0
        return tb - ta
      })
      setDbResults(sorted)
      setSavedTotalCount(total)
      setSavedPage(pg)
      setSavedPageSize(ps)
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Unknown error'
      notification.error({ message: 'Failed to load results', description: msg })
    } finally {
      setLoadingDbResults(false)
    }
  }

  const handleEvaluationModelSelectionChange = (values: CheckboxValueType[]): void => {
    const selected = values as string[]
    const normalized = enforceEvaluationSelectionLimit(selected)
    if (!normalized) {
      notification.warning({
        message: 'Evaluator Limit Reached',
        description: `You can select at most ${MAX_EVALUATORS} evaluator models.`,
      })
      return
    }
    setSelectedEvaluationModels(normalized)
  }

  const validateSelectedModelEligibility = (): boolean => {
    const invalidTranslationModels = selectedModels.filter(modelId => !availableModelIds.has(modelId))
    if (invalidTranslationModels.length > 0) {
      notification.error({
        message: 'Invalid Translation Model Selection',
        description: `These models are no longer eligible for AI Testing: ${invalidTranslationModels.join(', ')}`,
      })
      return false
    }

    const invalidEvaluationModels = selectedEvaluationModels.filter(modelId => !availableModelIds.has(modelId))
    if (invalidEvaluationModels.length > 0) {
      notification.error({
        message: 'Invalid Evaluator Selection',
        description: `These evaluators are no longer eligible for AI Testing: ${invalidEvaluationModels.join(', ')}`,
      })
      return false
    }

    if (proofreaderEnabled) {
      const invalidOverrides: string[] = []
      if (proofreaderFormalityModel && !availableModelIds.has(proofreaderFormalityModel)) {
        invalidOverrides.push(`Formality: ${proofreaderFormalityModel}`)
      }
      if (proofreaderGeneralModel && !availableModelIds.has(proofreaderGeneralModel)) {
        invalidOverrides.push(`General: ${proofreaderGeneralModel}`)
      }
      if (proofreaderAlignmentModel && !availableModelIds.has(proofreaderAlignmentModel)) {
        invalidOverrides.push(`Alignment: ${proofreaderAlignmentModel}`)
      }
      if (invalidOverrides.length > 0) {
        notification.error({
          message: 'Invalid Proofreader Override Selection',
          description: `These override models are no longer eligible for AI Testing: ${invalidOverrides.join(', ')}`,
        })
        return false
      }
    }

    return true
  }

  const handleSubmit = async (): Promise<void> => {
    if (!selectedEvaluationModels.length) {
      notification.warning({
        message: 'Evaluator Required',
        description: 'Please select at least one evaluator model.',
      })
      return
    }
    if (!validateSelectedModelEligibility()) {
      return
    }
    setLoading(true)
    setTranslationResults([])
    setEvaluationResults([])
    setTestErrors([])
    try {
      const data = await apiRunManualTest({
        system_msg: instruction,
        text_to_translate: textToTranslate,
        target_language: selectedLanguage,
        models: selectedModels,
        evaluation_models: selectedEvaluationModels,
        french_em_dash_enforce: enforceFrenchEmDash,
        french_em_dash_instructions: emDashInstructions,
        // Testing-only flag so backend can enable testing-specific logic
        testing: true,
      })
      setTranslationResults(data.translations)
      setEvaluationResults(data.evaluations)
      setTestErrors(data.errors ?? [])
      notification.success({
        message: 'AI Testing Complete',
        description: 'Results are now available below.',
      })
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Unknown error'
      notification.error({ message: 'AI Testing Failed', description: msg })
    } finally {
      setLoading(false)
    }
  }

  const handleImportProductionRules = async (): Promise<void> => {
    if (!selectedLanguage) {
      notification.warning({
        message: 'Select a target language',
        description: 'Choose a target language before importing production rules.',
      })
      return
    }

    setImportingProductionRules(true)
    try {
      const apiModel = selectedModels[0] || DEFAULT_MODEL_SELECTION[0]
      const response = await fetchProductionRules({
        target_language: selectedLanguage,
        source_language: 'English',
        genre: 'General fiction',
        quotation_style: 'default',
        api_model: apiModel,
      })
      const rawRules = response.rules_text || ''
      const hydratedRules = rawRules.replace(/\\n/g, '\n')
      const storedRules = hydratedRules.replace(/\r\n/g, '\n').replace(/\n/g, '\\n')
      setInstruction(storedRules)
      notification.success({
        message: 'Production rules imported',
        description: `${response.metadata.source_language} → ${response.metadata.target_language}`,
      })
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Unknown error'
      notification.error({
        message: 'Failed to import production rules',
        description: msg,
      })
    } finally {
      setImportingProductionRules(false)
    }
  }

const handleSubmitExtended = async (): Promise<void> => {
  if (!selectedInstructionId || selectedTextIds.length === 0) {
    notification.warning({
      message: 'Missing Selection',
      description: 'Please select both an instruction and at least one text to translate.',
    })
    return
  }
  if (!selectedEvaluationModels.length) {
    notification.warning({
      message: 'Evaluator Required',
      description: 'Please select at least one evaluator model.',
    })
    return
  }
  if (!validateSelectedModelEligibility()) {
    return
  }

  const instructionObj = instructions.find(i => i.id === selectedInstructionId)
  if (!instructionObj) return

  const selectedTexts = testTexts.filter(t => selectedTextIds.includes(t.id))

  // Prepare distributed batch request
  const batchRequest: DistributedBatchRequest = {
    instruction_id: selectedInstructionId,
    instruction_text: instructionObj.instruction,
    text_ids: selectedTextIds,
    texts: selectedTexts.map(t => ({
      id: t.id!,
      name: t.name,
      text: t.text,
      category: t.category
    })),
    target_language: selectedLanguage,
    translation_models: selectedModels,
    evaluation_models: selectedEvaluationModels,
    num_runs: numRuns,
    test_name: testName || `Batch: ${instructionObj.name}`,
    max_concurrent_jobs: 15,
    job_timeout_minutes: 20,
    french_em_dash_enforce: enforceFrenchEmDash,
    french_em_dash_instructions: emDashInstructions,
    proofreader_enabled: proofreaderEnabled,
    proofreader_formality_model: proofreaderEnabled && proofreaderFormalityModel ? proofreaderFormalityModel : undefined,
    proofreader_general_model: proofreaderEnabled && proofreaderGeneralModel ? proofreaderGeneralModel : undefined,
    proofreader_alignment_model: proofreaderEnabled && proofreaderAlignmentModel ? proofreaderAlignmentModel : undefined,
    // Testing-only flag so backend can enable testing-specific logic
    testing: true
  }

  setLoading(true)
  setTestErrors([])

  try {
    // Submit to distributed system
    console.log('[FRONTEND][AITestingTab.handleSubmitExtended] evaluation_models to submit:', batchRequest.evaluation_models)
    const response: DistributedBatchResponse = await runDistributedBatchTest(batchRequest)
    
    if (response.success) {
      // Set active batch for monitoring
      setActiveBatchId(response.batch_id)
      setShowBatchProgress(true)
      
      notification.success({
        message: 'Batch Job Submitted',
        description: `${response.jobs_queued}/${response.total_jobs} jobs queued. Estimated completion: ${response.estimated_duration_minutes} minutes.`,
        duration: 10
      })
      
      // Reset UI states
      setTranslationResults([])
      setEvaluationResults([])
      setMultiRunResults(null)
      setTestErrors([])
      
    } else {
      throw new Error('Failed to submit batch job')
    }

  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : 'Unknown error'
    notification.error({ 
      message: 'Batch Submission Failed', 
      description: msg,
      duration: 10
    })
  } finally {
    setLoading(false)
  }
}

// Handler for when distributed batch completes
const handleDistributedBatchComplete = async (batchId: string, results: any[]) => {
  try {
    setDistributedBatchResults(results)
    
    // Load the first result for display (similar to old behavior)
    if (results.length > 0) {
      const firstResult = results[0]
      if (firstResult.translation_results && firstResult.evaluation_results) {
        setTranslationResults(firstResult.translation_results)
        setEvaluationResults(firstResult.evaluation_results)
      }
      const combinedErrors: TestErrorEntry[] = [
        ...(firstResult.translation_errors ?? []),
        ...(firstResult.evaluation_errors ?? []),
      ]
      setTestErrors(combinedErrors)
    } else {
      setTestErrors([])
    }
    
    // Refresh database results
    await fetchDbResults()
    
    notification.success({
      message: 'Batch Processing Complete',
      description: `${results.length} test results are now available.`,
      duration: 10
    })
    
  } catch (error) {
    console.error('Error handling batch completion:', error)
  }
}

  const shouldDiscardEvaluation = (evalModel: string, evalResult: EvaluationResult | null): boolean => {
    if (!evalResult) return true
    if (deletedResults.has(evalModel)) return true
    if (hasAnyZeroScore(evalResult)) return true
    return false
  }

  const hasAllInvalidScores = (evalResult: EvaluationResult | null): boolean => {
    if (!evalResult) return true
    return Object.values(evalResult).every(cat => !isValidScore(cat.score))
  }

  const getAggregatedResults = (
    _language: string,
    _instructionId?: string,
    _testName?: string,
    _batchId?: string
  ): AggregatedScore[] => {
    return aggregatedScores
  }

  const getModelRankings = (
    language: string,
    instructionId?: string,
    testName?: string,
    batchId?: string
  ): ModelRanking[] => {
    // Prefer server-side aggregates (same source as Detailed Results tab) to ensure all models are included.
    // Fallback to local (paged) dbResults-based computation only if aggregates are not available.
    const usingAggregates =
      showAggregateModal &&
      hasLoadedAggregateData &&
      aggregateLoadRequest?.language === language &&
      (aggregateLoadRequest?.instructionId || '') === (instructionId || '') &&
      (aggregateLoadRequest?.testName || '') === (testName || '') &&
      (aggregateLoadRequest?.batchId || '') === (batchId || '') &&
      Array.isArray(aggregatedScores)

    if (usingAggregates) {
      // Group aggregated scores by base translation model
      const modelGroups = aggregatedScores.reduce((acc, item) => {
        const modelBase = item.model.split(' (eval:')[0]
        if (!acc[modelBase]) acc[modelBase] = []
        acc[modelBase].push(item)
        return acc
      }, {} as Record<string, typeof aggregatedScores>)

      const rankings: ModelRanking[] = Object.entries(modelGroups).map(([modelBase, items]) => {
        // Build per-category weighted averages across evaluators (weight = EVAL_WEIGHTS[evalModel] * count)
        const categoryMap = new Map<
          string,
          { sum: number; weight: number; min: number; max: number; count: number }
        >()

        items.forEach(s => {
          const evalModelMatch = s.model.match(/\(eval: (.+)\)/)
          const evalModel = evalModelMatch ? evalModelMatch[1] : 'Unknown'
          const evalWeight = (EVAL_WEIGHTS as Record<string, number>)[evalModel] ?? 1
          const count = s.count || 1
          const weight = evalWeight * count

          const entry = categoryMap.get(s.category) || {
            sum: 0,
            weight: 0,
            min: Number.POSITIVE_INFINITY,
            max: Number.NEGATIVE_INFINITY,
            count: 0,
          }
          entry.sum += s.average * weight
          entry.weight += weight
          entry.min = Math.min(entry.min, s.min)
          entry.max = Math.max(entry.max, s.max)
          entry.count += count
          categoryMap.set(s.category, entry)
        })

        const categoryBreakdown: Record<
          string,
          { average: number; min: number; max: number; count: number }
        > = {}
        categoryMap.forEach((v, cat) => {
          if (v.weight > 0) {
            categoryBreakdown[cat] = {
              average: v.sum / v.weight,
              min: isFinite(v.min) ? v.min : 0,
              max: isFinite(v.max) ? v.max : 0,
              count: v.count,
            }
          }
        })

        // Overall score computed to match Detailed Results tab:
        // - If any rows have category including "overall", compute weighted average across those rows.
        // - Otherwise compute weighted average across all rows.
        let overallScore = 0
        // Build row-level entries with evaluator weighting * count
        const rowsForOverall = (() => {
          const overallRows = items.filter(s => s.category.toLowerCase().includes('overall'))
          return overallRows.length ? overallRows : items
        })()
        const totals = rowsForOverall.reduce((acc, s) => {
          const evalModelMatch = s.model.match(/\(eval: (.+)\)/)
          const evalModel = evalModelMatch ? evalModelMatch[1] : 'Unknown'
          const evalWeight = (EVAL_WEIGHTS as Record<string, number>)[evalModel] ?? 1
          const count = s.count || 1
          const weight = evalWeight * count
          acc.weightedSum += (s.average ?? 0) * weight
          acc.totalWeight += weight
          return acc
        }, { weightedSum: 0, totalWeight: 0 })
        if (totals.totalWeight > 0) {
          overallScore = totals.weightedSum / totals.totalWeight
        }

        // Costs from aggregated evaluation/translation cost aggregates
        const costAgg = aggregatedEvalCosts.find(a => a.model === modelBase)
        const testCount = costAgg?.testCount ?? 0
        const transTotal = costAgg?.translationCostBreakdown?.totalCost ?? 0
        const avgCost = testCount > 0 ? transTotal / testCount : 0
        const costPerPoint = overallScore > 0 ? avgCost / overallScore : Infinity
        const efficiency = avgCost > 0 ? (overallScore * overallScore * overallScore) / avgCost : 0

        return {
          model: modelBase,
          overallScore,
          categoryBreakdown,
          avgCost,
          totalTests: testCount,
          costPerPoint,
          efficiency,
        }
      })

      return rankings.sort((a, b) => b.overallScore - a.overallScore)
    }

    // Fallback: compute from currently loaded (paged) dbResults
    return getModelRankingsFromUtils(dbResults, language, instructionId, calculateModelCostLocal)
  }

  const calculateModelCostLocal = (model: string, tokenInfo: TokenInfo): number => {
    return calculateModelCost(model, tokenInfo, modelCostRates)
  }

  // Aggregate evaluation costs by translation model (not used in rankings). Uses server-side aggregates.
  const getEvaluationCostAggregates = (
    _language: string,
    _instructionId?: string,
    _testName?: string,
    _batchId?: string
  ) => {
    return aggregatedEvalCosts
  }

  const getAvailableLanguages = (): string[] => {
    return Array.from(new Set(dbResults.map(r => r.language))).sort()
  }

  const getAvailableInstructionsForLanguage = (
    language: string,
    testName?: string
  ): Array<{ id: string; name: string; count: number }> => {
    if (!language) return []
    if (language === selectedLanguageForAggregate) {
      return aggregateInstructionOptions
    }
    return buildInstructionOptionsFromResults(
      dbResults,
      language,
      testName
    )
  }

  const getAvailableTestNamesForLanguage = (
    language: string,
    instructionId?: string
  ): Array<{ name: string; count: number }> => {
    if (!language) return []
    if (language === selectedLanguageForAggregate) {
      return aggregateTestNameOptions
    }
    return buildTestNameOptionsFromResults(
      dbResults,
      language,
      instructionId
    )
  }

  const getAvailableBatchesForLanguage = (
    language: string,
    instructionId?: string,
    testName?: string
  ): AggregateBatchOption[] => {
    if (!language) return []
    if (language === selectedLanguageForAggregate) {
      return aggregateBatchOptions
    }
    return buildBatchOptionsFromResults(
      dbResults,
      language,
      instructionId,
      testName
    )
  }

  const handleAggregateLanguageChange = (language: string): void => {
    setHasLoadedAggregateData(false)
    setAggregateFiltersDirty(true)
    setAggregatedScores([])
    setAggregatedEvalCosts([])
    setAggregateLoadRequest(null)
    setSelectedInstructionForAggregate('')
    setSelectedTestNameForAggregate('')
    setSelectedBatchForAggregate('')
    setSelectedLanguageForAggregate(language)
  }

  const handleAggregateInstructionChange = (instructionId?: string): void => {
    setHasLoadedAggregateData(false)
    setAggregateFiltersDirty(true)
    setAggregatedScores([])
    setAggregatedEvalCosts([])
    setAggregateLoadRequest(null)
    setSelectedInstructionForAggregate(instructionId || '')
    setSelectedBatchForAggregate('')
  }

  const handleAggregateTestNameChange = (testName?: string): void => {
    setHasLoadedAggregateData(false)
    setAggregateFiltersDirty(true)
    setAggregatedScores([])
    setAggregatedEvalCosts([])
    setAggregateLoadRequest(null)
    setSelectedTestNameForAggregate(testName || '')
    setSelectedBatchForAggregate('')
  }

  const handleAggregateBatchChange = (batchId?: string): void => {
    setHasLoadedAggregateData(false)
    setAggregateFiltersDirty(true)
    setAggregatedScores([])
    setAggregatedEvalCosts([])
    setAggregateLoadRequest(null)
    setSelectedBatchForAggregate(batchId || '')
  }

  const loadAggregateDataForSelection = (): void => {
    if (!selectedLanguageForAggregate) return
    setAggregateFiltersDirty(false)
    setHasLoadedAggregateData(false)
    setAggregateLoadRequest({
      language: selectedLanguageForAggregate,
      instructionId: selectedInstructionForAggregate || undefined,
      testName: selectedTestNameForAggregate || undefined,
      batchId: selectedBatchForAggregate || undefined,
      requestId: Date.now(),
    })
  }

  const displayResults = useMemo(() => {
    if (multiRunResults) {
      const avgTranslationResults: TranslationResult[] = Object.values(multiRunResults.aggregatedTranslations).map(agg => ({
        model: agg.model,
        status: 'success',
        parsed_response: `[Average of ${multiRunResults.totalRuns} runs]`,
          token_info: {
            input_tokens: Math.round(agg.avgTokens * 0.7),
            output_tokens: Math.round(agg.avgTokens * 0.3),
            total_tokens: Math.round(agg.avgTokens),
            reasoning_tokens: 0,
            cached_tokens: 0,
            model: modelApiById[agg.model] || agg.model
          }
        }))

      const avgEvaluationResults: ModelResult[] = Object.values(multiRunResults.aggregatedEvaluations).map(agg => {
        const avgEvalJson: EvaluationResult = {}
        Object.entries(agg.categories).forEach(([category, data]) => {
          avgEvalJson[category] = {
            score: data.average,
            comment: `Average: ${data.average.toFixed(2)} (Min: ${data.min}, Max: ${data.max}, ${data.scores.length} runs)`
          }
        })
        return { model: agg.model, status: 'success', response: JSON.stringify(avgEvalJson) }
      })

      return { translations: avgTranslationResults, evaluations: avgEvaluationResults }
    }
    return { translations: translationResults, evaluations: evaluationResults }
  }, [multiRunResults, translationResults, evaluationResults, modelApiById])

  const groupedResults: Record<string, { evalModel: string; result: ModelResult; isDiscarded: boolean }[]> =
    displayResults.evaluations.reduce((acc, r) => {
      const [selModel, evalModel] = r.model.split('::', 2)
      const evalJson = parseEvaluationResult(r.response)
      const isDiscarded = shouldDiscardEvaluation(r.model, evalJson)
      if (!acc[selModel]) acc[selModel] = []
      acc[selModel].push({ evalModel, result: r, isDiscarded })
      return acc
    }, {} as Record<string, { evalModel: string; result: ModelResult; isDiscarded: boolean }[]>)

  const overallScores = useMemo<Record<string, number>>(() => {
    const scores: Record<string, number> = {}
    Object.entries(groupedResults).forEach(([selModel, items]) => {
      const categorySet = new Set<string>()
      items.forEach(({ result, isDiscarded }) => {
        if (isDiscarded) return
        const evalJson = parseEvaluationResult(result.response)
        if (evalJson) Object.keys(evalJson).forEach(cat => categorySet.add(cat))
      })
      const categories = Array.from(categorySet)
      if (categories.length === 0) {
        scores[selModel] = 0
        return
      }
      let weightedSumTotal = 0
      let totalCategoryWeight = 0
      categories.forEach(category => {
        let weightedScoreSum = 0
        let weightSumForCategory = 0
        items.forEach(({ evalModel, result, isDiscarded }) => {
          if (isDiscarded) return
          const evalJson = parseEvaluationResult(result.response)
          if (!evalJson) return
          const entry = evalJson[category]
          const evalWeight = EVAL_WEIGHTS[evalModel] ?? 1
          if (entry && isValidScore(entry.score)) {
            weightedScoreSum += entry.score * evalWeight
            weightSumForCategory += evalWeight
          }
        })
        if (weightSumForCategory > 0) {
          const averageForCat = weightedScoreSum / weightSumForCategory
          weightedSumTotal += averageForCat
          totalCategoryWeight += 1
        }
      })
      scores[selModel] = totalCategoryWeight > 0 ? weightedSumTotal / totalCategoryWeight : 0
    })
    return scores
  }, [groupedResults, deletedResults])

  const openTranslationModal = (modelName: string): void => {
    const entry = displayResults.translations.find(tr => tr.model === modelName)
    const parsed = entry ? (entry.parsed_response || (entry as any).response || 'Translation not found.') : 'Translation not found.'
    setTranslationToShow(parsed)
    setShowTranslationModal(true)
  }

  const openSideBySideModal = (modelName: string): void => {
    const entry = translationResults.find(tr => tr.model === modelName)
    const parsed = entry ? (entry.parsed_response || (entry as any).response || 'Translation not found.') : 'Translation not found.'
    setSideBySideTranslation(parsed)
    setShowSideBySideModal(true)
  }

  const openEmDashCompare = (modelName: string): void => {
    const entry = translationResults.find(tr => tr.model === modelName) as any
    if (!entry) {
      notification.warning({ message: 'No translation found', description: 'Unable to locate translation for the selected model.' })
      return
    }
    const before = (entry.pre_em_dash_parsed_response || entry.pre_em_dash_response || '').toString()
    const after = (entry.parsed_response || entry.response || '').toString()
    if (!before || !after) {
      notification.warning({ message: 'No before/after available', description: 'This translation does not have em-dash pre/post data.' })
      return
    }
    setEmDashBeforeHTML(before)
    setEmDashAfterHTML(after)
    setShowEmDashCompareModal(true)
  }

  const onCompareCheckboxChange = (checkedValues: CheckboxValueType[]): void => {
    const selected = checkedValues as string[]
    if (selected.length > 2) {
      notification.warning({
        message: 'Only Two Allowed',
        description: 'Please select exactly two models to compare.',
      })
      return
    }
    setSelectedCompareModels(selected)
  }

  const handleCompare = async (): Promise<void> => {
    if (selectedCompareModels.length !== 2) {
      notification.warning({
        message: 'Select Two Models',
        description: 'You must select exactly two translations to compare.',
      })
      return
    }
    const [modelA, modelB] = selectedCompareModels
    const transA = translationResults.find(t => t.model === modelA)?.parsed_response || ''
    const transB = translationResults.find(t => t.model === modelB)?.parsed_response || ''
    try {
      const data = await compareTranslations({
        original_text: textToTranslate,
        model1: modelA,
        translation1: transA,
        model2: modelB,
        translation2: transB,
      })
      setCompareResult(data)
      setShowCompareModal(true)
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Unknown error'
      notification.error({ message: 'Comparison Failed', description: msg })
    }
  }

  const { costBreakdowns } = useMemo(() => {
    const breakdownMap: Record<string, {
      inputTokens: number
      cachedTokens: number
      outputTokens: number
      reasoningTokens: number
      inputCost: number
      cachedCost: number
      outputCost: number
      reasoningCost: number
      totalCost: number
      // New: evaluation-only aggregates (kept separate from translation)
      evalInputTokens: number
      evalCachedTokens: number
      evalOutputTokens: number
      evalReasoningTokens: number
      evalTotalCost: number
    }> = {}

    if (multiRunResults) {
      Object.entries(multiRunResults.aggregatedTranslations).forEach(([model, aggData]) => {
        const totalCost = aggData.totalCost
        const avgTokens = aggData.avgTokens
        const estimatedInputTokens = Math.round(avgTokens * 0.7)
        const estimatedOutputTokens = Math.round(avgTokens * 0.3)

        breakdownMap[model] = {
          inputTokens: estimatedInputTokens,
          cachedTokens: 0,
          outputTokens: estimatedOutputTokens,
          reasoningTokens: 0,
          inputCost: totalCost * 0.7,
          cachedCost: 0,
          outputCost: totalCost * 0.3,
          reasoningCost: 0,
          totalCost: totalCost,
          // No eval aggregation available in multi-run averages (legacy); show zeros
          evalInputTokens: 0,
          evalCachedTokens: 0,
          evalOutputTokens: 0,
          evalReasoningTokens: 0,
          evalTotalCost: 0
        }
      })
    } else {
      // Translation costs (unchanged semantics)
      translationResults.forEach(tr => {
        if (!tr.token_info) return
        const { input_tokens, output_tokens, cached_tokens = 0, reasoning_tokens = 0, model: apiModel } = tr.token_info
        const rates = modelCostRates[tr.model] || modelCostRates[apiModel]
        if (!rates) return
        const nonCached = input_tokens - cached_tokens
        const nonReasoningOutput = output_tokens - reasoning_tokens

        const inputCost = (nonCached / 1_000_000) * rates.input
        const cachedCost = rates.cachedInput
          ? (cached_tokens / 1_000_000) * rates.cachedInput
          : (cached_tokens / 1_000_000) * rates.input * 0.1
        const outputCost = (nonReasoningOutput / 1_000_000) * rates.output
        const reasoningCost = (reasoning_tokens / 1_000_000) * rates.output
        const totalCost = inputCost + cachedCost + outputCost + reasoningCost

        breakdownMap[tr.model] = {
          inputTokens: nonCached,
          cachedTokens: cached_tokens,
          outputTokens: nonReasoningOutput,
          reasoningTokens: reasoning_tokens,
          inputCost,
          cachedCost,
          outputCost,
          reasoningCost,
          totalCost,
          // Initialize eval fields; will be filled from evaluationResults below
          evalInputTokens: 0,
          evalCachedTokens: 0,
          evalOutputTokens: 0,
          evalReasoningTokens: 0,
          evalTotalCost: 0
        }
      })

      // Evaluation costs: sum across evaluators for each selected translation model
      evaluationResults.forEach(er => {
        const parts = er.model.split('::', 2)
        const selModel = parts[0]
        const evalModel = parts[1] || parts[0]
        const ti = er.token_info
        if (!selModel || !ti) return

        if (!breakdownMap[selModel]) {
          // Create a zeroed translation breakdown so we can surface evaluation costs even if translation token info is missing
          breakdownMap[selModel] = {
            inputTokens: 0,
            cachedTokens: 0,
            outputTokens: 0,
            reasoningTokens: 0,
            inputCost: 0,
            cachedCost: 0,
            outputCost: 0,
            reasoningCost: 0,
            totalCost: 0,
            evalInputTokens: 0,
            evalCachedTokens: 0,
            evalOutputTokens: 0,
            evalReasoningTokens: 0,
            evalTotalCost: 0
          }
        }

        const evalCost = calculateCost(evalModel, ti, modelCostRates)

        const cached = ti.cached_tokens || 0
        const nonCached = (ti.input_tokens || 0) - cached
        const reasoning = ti.reasoning_tokens || 0
        const nonReasoningOutput = (ti.output_tokens || 0) - reasoning

        breakdownMap[selModel].evalInputTokens += nonCached
        breakdownMap[selModel].evalCachedTokens += cached
        breakdownMap[selModel].evalOutputTokens += nonReasoningOutput
        breakdownMap[selModel].evalReasoningTokens += reasoning
        breakdownMap[selModel].evalTotalCost += evalCost
      })
    }
    return { costBreakdowns: breakdownMap }
  }, [translationResults, evaluationResults, multiRunResults, modelCostRates])

  const downloadAggregateResults = (): void => {
    if (!selectedLanguageForAggregate) {
      notification.warning({
        message: 'No Language Selected',
        description: 'Please select a language to download aggregate results.',
      })
      return
    }
    const aggregatedData = getAggregatedResults(
      selectedLanguageForAggregate,
      selectedInstructionForAggregate,
      selectedTestNameForAggregate,
      selectedBatchForAggregate
    )
    const filteredResults = dbResults.filter(r => {
      if (r.language !== selectedLanguageForAggregate) return false
      if (selectedInstructionForAggregate && r.instruction_id !== selectedInstructionForAggregate) return false
      if (selectedTestNameForAggregate && r.test_name !== selectedTestNameForAggregate) return false
      if (selectedBatchForAggregate && r.batch_id !== selectedBatchForAggregate) return false
      return true
    })

    const totalTests = aggregateTotalCount
    const instructionInfo = selectedInstructionForAggregate
      ? getAvailableInstructionsForLanguage(selectedLanguageForAggregate, selectedTestNameForAggregate)
        .find(inst => inst.id === selectedInstructionForAggregate)?.name || 'Unknown Instruction'
      : 'All Instructions'
    const testNameInfo = selectedTestNameForAggregate || 'All Test Names'
    const batchInfo = selectedBatchForAggregate
      ? getAvailableBatchesForLanguage(
        selectedLanguageForAggregate,
        selectedInstructionForAggregate || undefined,
        selectedTestNameForAggregate || undefined
      ).find(batch => batch.id === selectedBatchForAggregate)?.name || selectedBatchForAggregate
      : 'All Batches'

    let csvContent = `Aggregate Results for ${selectedLanguageForAggregate}\n`
    csvContent += `Instruction Set: ${instructionInfo}\n`
    csvContent += `Test Name: ${testNameInfo}\n`
    csvContent += `Batch: ${batchInfo}\n`
    csvContent += `Total Tests: ${totalTests}\n`
    csvContent += `Generated: ${new Date().toLocaleString()}\n\n`
    csvContent += 'Model,Evaluator,Category,Average Score,Min Score,Max Score,Sample Count\n'

    aggregatedData.forEach(item => {
      const modelBase = item.model.split(' (eval:')[0]
      const evalModelMatch = item.model.match(/\(eval: (.+)\)/)
      const evalModel = evalModelMatch ? evalModelMatch[1] : 'Unknown'
      csvContent += `"${modelBase}","${evalModel}","${item.category}",${item.average.toFixed(2)},${item.min},${item.max},${item.count}\n`
    })

    csvContent += '\n\nModel Summary (Overall Averages)\n'
    csvContent += 'Model,Overall Average\n'

    const modelGroups = aggregatedData.reduce((acc, item) => {
      const modelBase = item.model.split(' (eval:')[0]
      if (!acc[modelBase]) acc[modelBase] = []
      acc[modelBase].push(item)
      return acc
    }, {} as Record<string, AggregatedScore[]>)

    Object.entries(modelGroups).forEach(([modelBase, scores]) => {
      const allAverages = scores.map(s => s.average)
      const overallAvg = allAverages.reduce((sum, avg) => sum + avg, 0) / allAverages.length
      csvContent += `"${modelBase}",${overallAvg.toFixed(2)}\n`
    })

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    const instructionSlug = selectedInstructionForAggregate
      ? instructionInfo.replace(/[^a-zA-Z0-9]/g, '_')
      : 'all_instructions'
    const testNameSlug = selectedTestNameForAggregate
      ? selectedTestNameForAggregate.replace(/[^a-zA-Z0-9]/g, '_')
      : 'all_test_names'
    const batchSlug = selectedBatchForAggregate
      ? selectedBatchForAggregate.replace(/[^a-zA-Z0-9]/g, '_')
      : 'all_batches'
    const fileName = `aggregate_results_${selectedLanguageForAggregate}_${instructionSlug}_${testNameSlug}_${batchSlug}_${Date.now()}.csv`
    link.setAttribute('href', url)
    link.setAttribute('download', fileName)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    notification.success({
      message: 'Download Complete',
      description: `Aggregate results for ${selectedLanguageForAggregate} (${instructionInfo}, ${testNameInfo}, ${batchInfo}) have been downloaded.`,
    })
  }

  const downloadAllAggregateResults = (): void => {
    const languages = getAvailableLanguages()
    if (languages.length === 0) {
      notification.warning({
        message: 'No Data Available',
        description: 'No saved test results to export.',
      })
      return
    }
    const exportData = {
      exportDate: new Date().toISOString(),
      totalLanguages: languages.length,
      totalTests: dbResults.length,
      languageInstructionCombinations: languages.map(lang => {
        const rowsForLanguage = dbResults.filter(r => r.language === lang)
        const instructionsForLang = getAvailableInstructionsForLanguage(lang)
        return {
          language: lang,
          totalTestsForLanguage: rowsForLanguage.length,
          instructions: instructionsForLang.map(inst => {
            const rowsForInstruction = rowsForLanguage.filter(
              r => (r.instruction_id || '').trim() === (inst.id || '').trim()
            )
            const testNameGroups = rowsForInstruction.reduce((acc, row) => {
              const name = (row.test_name || '').trim() || '(unnamed)'
              if (!acc[name]) {
                acc[name] = {
                  name,
                  count: 0,
                  batchIds: new Set<string>(),
                  latestCreatedAt: '',
                }
              }
              acc[name].count += 1
              const batchId = (row.batch_id || '').trim()
              if (batchId) {
                acc[name].batchIds.add(batchId)
              }
              const candidateTs = new Date(row.created_at || '').getTime()
              const currentTs = new Date(acc[name].latestCreatedAt || '').getTime()
              if (!Number.isNaN(candidateTs) && (Number.isNaN(currentTs) || candidateTs > currentTs)) {
                acc[name].latestCreatedAt = row.created_at
              }
              return acc
            }, {} as Record<string, { name: string; count: number; batchIds: Set<string>; latestCreatedAt: string }>)

            const proofreaderGroups = rowsForInstruction.reduce((acc, row) => {
              const formalityModelStored = (row.proofreader_formality_model || '').trim()
              const generalModelStored = (row.proofreader_general_model || '').trim()
              const alignmentModelStored = (row.proofreader_alignment_model || '').trim()

              const formalityModel =
                formalityModelStored ||
                (row.translation_results || [])
                  .map(tr => ((tr as any)?.formality_proofreader_result?.model_id || '').trim())
                  .find(Boolean) ||
                ''
              const generalModel =
                generalModelStored ||
                (row.translation_results || [])
                  .map(tr => ((tr as any)?.general_proofreader_result?.model_id || '').trim())
                  .find(Boolean) ||
                ''
              const alignmentModel = alignmentModelStored

              const hasArtifacts = Boolean(
                formalityModel ||
                generalModel ||
                alignmentModel ||
                (row.translation_results || []).some(tr => {
                  const entry = tr as any
                  return (
                    typeof entry?.formality_proofreader_status === 'string' ||
                    typeof entry?.general_proofreader_status === 'string' ||
                    Boolean(entry?.formality_proofreader_result) ||
                    Boolean(entry?.general_proofreader_result)
                  )
                })
              )

              const hasExplicitFlag = typeof row.proofreader_enabled === 'boolean'
              if (!hasExplicitFlag && !hasArtifacts) return acc

              const enabled = hasExplicitFlag
                ? Boolean(row.proofreader_enabled)
                : hasArtifacts
              const key = `${enabled ? 'enabled' : 'disabled'}|${formalityModel}|${generalModel}|${alignmentModel}`
              if (!acc[key]) {
                acc[key] = {
                  proofreaderEnabled: enabled,
                  formalityModel: formalityModel || null,
                  generalModel: generalModel || null,
                  alignmentModel: alignmentModel || null,
                  count: 0,
                  batchIds: new Set<string>(),
                  testNames: new Set<string>(),
                }
              }
              acc[key].count += 1
              const batchId = (row.batch_id || '').trim()
              if (batchId) {
                acc[key].batchIds.add(batchId)
              }
              const testName = (row.test_name || '').trim()
              if (testName) {
                acc[key].testNames.add(testName)
              }
              return acc
            }, {} as Record<
              string,
              {
                proofreaderEnabled: boolean
                formalityModel: string | null
                generalModel: string | null
                alignmentModel: string | null
                count: number
                batchIds: Set<string>
                testNames: Set<string>
              }
            >)

            const aggregatedData = getAggregatedResults(lang, inst.id)
            const modelGroups = aggregatedData.reduce((acc, item) => {
              const modelBase = item.model.split(' (eval:')[0]
              if (!acc[modelBase]) {
                acc[modelBase] = { model: modelBase, categories: {}, evaluators: {} as Record<string, any[]> }
              }
              const evalModelMatch = item.model.match(/\(eval: (.+)\)/)
              const evalModel = evalModelMatch ? evalModelMatch[1] : 'Unknown'
              if (!acc[modelBase].categories[item.category]) acc[modelBase].categories[item.category] = []
              acc[modelBase].categories[item.category].push({
                evaluator: evalModel,
                average: item.average,
                min: item.min,
                max: item.max,
                count: item.count,
              })
              if (!acc[modelBase].evaluators[evalModel]) acc[modelBase].evaluators[evalModel] = []
              acc[modelBase].evaluators[evalModel].push({
                category: item.category,
                average: item.average,
                min: item.min,
                max: item.max,
                count: item.count,
              })
              return acc
            }, {} as Record<string, any>)
            return {
              instructionId: inst.id,
              instructionName: inst.name,
              testCount: inst.count,
              testNames: Object.values(testNameGroups)
                .map(group => ({
                  name: group.name,
                  count: group.count,
                  batchIds: Array.from(group.batchIds).sort(),
                  latestCreatedAt: group.latestCreatedAt || null,
                }))
                .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name)),
              proofreaderUsage: Object.values(proofreaderGroups)
                .map(group => ({
                  proofreaderEnabled: group.proofreaderEnabled,
                  formalityModel: group.formalityModel,
                  generalModel: group.generalModel,
                  alignmentModel: group.alignmentModel,
                  count: group.count,
                  batchIds: Array.from(group.batchIds).sort(),
                  testNames: Array.from(group.testNames).sort(),
                }))
                .sort((a, b) => b.count - a.count),
              models: Object.values(modelGroups),
            }
          }),
        }
      }),
    }

    const jsonBlob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json;charset=utf-8;'
    })
    const link = document.createElement('a')
    const url = URL.createObjectURL(jsonBlob)
    link.setAttribute('href', url)
    link.setAttribute('download', `all_aggregate_results_by_language_and_instruction_${Date.now()}.json`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    notification.success({
      message: 'Download Complete',
      description: 'All aggregate results were downloaded with test names and proofreader usage metadata when available.',
    })
  }

  const handleInstructionSubmit = async (values: any): Promise<void> => {
    try {
      if (editingInstruction) {
        await apiUpdateInstruction(editingInstruction.id, { ...values, language: selectedLanguage })
      } else {
        await apiSaveInstruction({ ...values, language: selectedLanguage })
      }
      notification.success({ message: `Instruction ${editingInstruction ? 'updated' : 'created'} successfully` })
      setShowInstructionModal(false)
      instructionForm.resetFields()
      setEditingInstruction(null)
      fetchInstructions()
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Failed to save instruction'
      notification.error({ message: 'Failed to save instruction', description: msg })
    }
  }

  const handleTextSubmit = async (values: any): Promise<void> => {
    try {
      if (editingText) {
        await apiUpdateText(editingText.id, values)
      } else {
        await apiSaveText(values)
      }
      notification.success({ message: `Text ${editingText ? 'updated' : 'created'} successfully` })
      setShowTextModal(false)
      textForm.resetFields()
      setEditingText(null)
      fetchTestTexts()
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Failed to save text'
      notification.error({ message: 'Failed to save text', description: msg })
    }
  }

  const deleteInstruction = async (id: string): Promise<void> => {
    try {
      await apiDeleteInstruction(id)
      notification.success({ message: 'Instruction deleted successfully' })
      fetchInstructions()
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Unknown error'
      notification.error({ message: 'Failed to delete instruction', description: msg })
    }
  }

  const deleteText = async (id: string): Promise<void> => {
    try {
      await apiDeleteText(id)
      notification.success({ message: 'Text deleted successfully' })
      fetchTestTexts()
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Unknown error'
      notification.error({ message: 'Failed to delete text', description: msg })
    }
  }

  const aggregateMultiRunResults = async (testName: string, language: string): Promise<MultiRunAggregatedResult | null> => {
    setLoadingMultiRun(true)
    try {
      const allResults = await apiFetchDbResults(language)
      const testRuns = allResults.filter(r => r.test_name === testName)
      if (testRuns.length === 0) return null

      const firstRun = testRuns[0]
      const aggregated: MultiRunAggregatedResult = {
        testName,
        language,
        instructionId: firstRun.instruction_id,
        textId: firstRun.text_id,
        models: firstRun.models,
        totalRuns: firstRun.total_runs,
        aggregatedTranslations: {},
        aggregatedEvaluations: {},
        individualRuns: []
      }

      testRuns.forEach(run => {
        run.models.forEach(modelName => {
          const translation = run.translation_results.find(tr => tr.model === modelName)
          const modelEvaluations = run.evaluation_results.filter(er => er.model.startsWith(`${modelName}::`))
          if (translation) {
            aggregated.individualRuns.push({
              runNumber: run.run_number,
              translation,
              evaluations: modelEvaluations
            })
          }
        })
      })

      testRuns.forEach(run => {
        run.translation_results.forEach(tr => {
          if (!aggregated.aggregatedTranslations[tr.model]) {
            aggregated.aggregatedTranslations[tr.model] = {
              model: tr.model,
              avgTokens: 0,
              totalCost: 0
            }
          }
          if (tr.token_info) {
            const cost = calculateCost(tr.model, tr.token_info, modelCostRates)
            aggregated.aggregatedTranslations[tr.model].totalCost += cost
            aggregated.aggregatedTranslations[tr.model].avgTokens += tr.token_info.total_tokens
          }
        })
      })

      Object.values(aggregated.aggregatedTranslations).forEach(agg => {
        agg.avgTokens = agg.avgTokens / testRuns.length
        agg.totalCost = agg.totalCost / testRuns.length
      })

      testRuns.forEach(run => {
        run.evaluation_results.forEach(evalResult => {
          const [selModel, evalModel] = evalResult.model.split('::', 2)
          const key = `${selModel}::${evalModel}`
          if (!aggregated.aggregatedEvaluations[key]) {
            aggregated.aggregatedEvaluations[key] = {
              model: evalResult.model,
              categories: {},
              overallAverage: 0
            }
          }
          const evalJson = parseEvaluationResult(evalResult.response)
          if (evalJson && !hasAnyZeroScore(evalJson)) {
            Object.entries(evalJson).forEach(([category, entry]) => {
              if (!isValidScore(entry.score)) return
              if (!aggregated.aggregatedEvaluations[key].categories[category]) {
                aggregated.aggregatedEvaluations[key].categories[category] = {
                  scores: [],
                  average: 0,
                  min: Infinity,
                  max: -Infinity
                }
              }
              const catData = aggregated.aggregatedEvaluations[key].categories[category]
              catData.scores.push(entry.score)
              catData.min = Math.min(catData.min, entry.score)
              catData.max = Math.max(catData.max, entry.score)
            })
          }
        })
      })

      Object.values(aggregated.aggregatedEvaluations).forEach(evalAgg => {
        let totalScore = 0
        let categoryCount = 0
        Object.values(evalAgg.categories).forEach(cat => {
          cat.average = cat.scores.reduce((a, b) => a + b, 0) / cat.scores.length
          totalScore += cat.average
          categoryCount++
        })
        evalAgg.overallAverage = categoryCount > 0 ? totalScore / categoryCount : 0
      })

      setMultiRunResults(aggregated)
      setTestErrors([])
      return aggregated
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Unknown error'
      notification.error({ message: 'Failed to aggregate results', description: msg })
      return null
    } finally {
      setLoadingMultiRun(false)
    }
  }

  const enhancedCostAnalysis = useMemo(() => {
    const QUALITY_THRESHOLD = 7.0
    const PENALTY_FACTOR = 0.1
    const analysis: {
      model: string
      cost: number
      score: number
      costPerPoint: number
      valueRating: number
      breakdown: any
      rank: number
      efficiency: number
      qualityPenalty: boolean
    }[] = []

    Object.entries(costBreakdowns).forEach(([model, breakdown]) => {
      const cost = breakdown.totalCost
      const score = overallScores[model] ?? 0
      const costPerPoint = score > 0 ? cost / score : Infinity
      let efficiency = 0
      let qualityPenalty = false
      if (cost > 0) {
        if (score < QUALITY_THRESHOLD) {
          efficiency = (score * score) / cost * PENALTY_FACTOR
          qualityPenalty = true
        } else {
          efficiency = (score * score * score) / cost
        }
      }
      analysis.push({
        model,
        cost,
        score,
        costPerPoint,
        valueRating: 0,
        breakdown,
        rank: 0,
        efficiency,
        qualityPenalty
      })
    })

    analysis.sort((a, b) => b.efficiency - a.efficiency)
    analysis.forEach((item, index) => {
      item.rank = index + 1
      if (item.score < 7.0) item.valueRating = 1
      else if (index === 0) item.valueRating = 5
      else if (index === 1) item.valueRating = 4
      else if (index < analysis.length * 0.3) item.valueRating = 3
      else if (index < analysis.length * 0.6) item.valueRating = 2
      else item.valueRating = 1
    })
    return analysis
  }, [costBreakdowns, overallScores])

  return (
    <div className="ai-testing-page">
      {/* Header Section */}
      <Card className="ai-testing-header-card">
        <Row justify="space-between" align="middle">
          <Col>
            <div className="ai-testing-kicker">Admin workflow</div>
            <Title level={3} className="ai-testing-title">AI Translation Testing</Title>
            <Text type="secondary">Configure model tests, review saved results, and monitor batch cost.</Text>
          </Col>
          <Col>
            <Space size="large">
              <Button
                icon={<FolderOpenOutlined />}
                onClick={() => setShowSavedModal(true)}
                loading={loadingDbResults}
              >
                View Saved ({savedTotalCount})
              </Button>
              <Button
                icon={<BarChartOutlined />}
                onClick={() => {
                  setSelectedLanguageForAggregate(selectedLanguage)
                  setSelectedInstructionForAggregate('')
                  setSelectedTestNameForAggregate('')
                  setSelectedBatchForAggregate('')
                  setAggregateFiltersDirty(true)
                  setHasLoadedAggregateData(false)
                  setAggregatedScores([])
                  setAggregatedEvalCosts([])
                  setAggregateLoadRequest(null)
                  setShowAggregateModal(true)
                }}
                disabled={!dbResults.length}
              >
                Aggregate Results
              </Button>
              <Button
                onClick={() => setShowActiveBatches(true)}
                disabled={!dbResults.length}
              >
                Active Batches
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => fetchDbResults(1, savedPageSize)}
                loading={loadingDbResults}
              >
                Refresh Results
              </Button>
              <Popconfirm
                title={`Delete all tests for ${selectedLanguage}?`}
                onConfirm={deleteAllTestsForLanguage}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  icon={<DeleteOutlined />}
                  danger
                  loading={loadingDeleteAll}
                  disabled={savedTotalCount === 0}
                >
                  Delete All for Language
                </Button>
              </Popconfirm>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Configuration Section */}
      <Card title="Test Configuration" className="ai-testing-section-card">
        <Row gutter={[24, 24]}>
          <Col span={8}>
            <Title level={5}>Target Language</Title>
            <Select
              style={{ width: '100%' }}
              value={selectedLanguage}
              onChange={setSelectedLanguage}
              size="large"
            >
              {[
                'Dutch', 'English', 'British English', 'French', 'German', 'Italian',
                'European Portuguese', 'Brazilian Portuguese', 'European Spanish',
                'Latin American Spanish', 'Japanese', 'Korean', 'Traditional Chinese',
                'Simplified Chinese', 'Russian', 'Turkish', 'Thai', 'Hindi', 'Catalan', 'Polish',
                'Ukranian', 'Swedish', 'Afrikaans', 'Hebrew', 'Norweigan', 'Serbian',
                'Czech', 'Danish', 'Bosnian', 'Croatian', 'Finnish', 'Icelandic', 'Bulgarian'
              ].map(lang => (
                <Option key={lang} value={lang}>{lang}</Option>
              ))}
            </Select>
            {selectedLanguage === 'French' && (
              <div style={{ marginTop: 8 }}>
                <Checkbox
                  checked={enforceFrenchEmDash}
                  onChange={(e) => setEnforceFrenchEmDash(e.target.checked)}
                >
                  Run chapter-level French dialogue normalizer (change-only) before evaluation
                </Checkbox>
                {enforceFrenchEmDash && (
                  <div style={{ marginTop: 8 }}>
                    <Title level={5} style={{ marginBottom: 4 }}>
                      Dialogue normalizer instructions override (optional)
                    </Title>
                    <TextArea
                      rows={4}
                      placeholder="Provide optional additional instructions for the dialogue normalizer..."
                      value={emDashInstructions}
                      onChange={(e) => setEmDashInstructions(e.target.value)}
                    />
                  </div>
                )}
              </div>
            )}
            <div style={{ marginTop: 8 }}>
              <Checkbox
                checked={proofreaderEnabled}
                onChange={(e) => setProofreaderEnabled(e.target.checked)}
              >
                Run proofreader passes (formality/general) for this batch
              </Checkbox>
              {proofreaderEnabled && (
                <div style={{ marginTop: 12 }}>
                  <Title level={5} style={{ marginBottom: 8 }}>Proofreader Model Overrides (Optional)</Title>
                  <Space direction="vertical" style={{ width: '100%' }} size="small">
                    <Text type="secondary">
                      Leave any field blank to use the normally configured system proofreader model for that stage.
                    </Text>
                    <Select
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      placeholder="Formality stage (default configured model)"
                      value={proofreaderFormalityModel || undefined}
                      onChange={(value) => setProofreaderFormalityModel((value as string) || '')}
                    >
                      {modelCatalog.map(model => (
                        <Option key={`proof-formality-${model.id}`} value={model.id}>{model.id}</Option>
                      ))}
                    </Select>
                    <Select
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      placeholder="General stage (default configured model)"
                      value={proofreaderGeneralModel || undefined}
                      onChange={(value) => setProofreaderGeneralModel((value as string) || '')}
                    >
                      {modelCatalog.map(model => (
                        <Option key={`proof-general-${model.id}`} value={model.id}>{model.id}</Option>
                      ))}
                    </Select>
                    <Select
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      placeholder="Alignment stage (default configured model)"
                      value={proofreaderAlignmentModel || undefined}
                      onChange={(value) => setProofreaderAlignmentModel((value as string) || '')}
                    >
                      {modelCatalog.map(model => (
                        <Option key={`proof-alignment-${model.id}`} value={model.id}>{model.id}</Option>
                      ))}
                    </Select>
                  </Space>
                </div>
              )}
            </div>
          </Col>
          <Col span={8}>
            <Title level={5}>Number of Runs</Title>
            <InputNumber
              min={1}
              max={10}
              value={numRuns}
              onChange={(value) => setNumRuns(value || 1)}
              style={{ width: '100%' }}
              size="large"
            />
          </Col>
          <Col span={8}>
            <Title level={5}>Test Name (Optional)</Title>
            <Input
              placeholder="Enter test name"
              value={testName}
              onChange={(e) => setTestName(e.target.value)}
              size="large"
            />
          </Col>
        </Row>
      </Card>

      {/* Database Testing Section */}
      <Card title="Database Testing (Recommended)" className="ai-testing-section-card">
        <Row gutter={[24, 24]}>
          {/* Instructions */}
          <Col span={12}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Row justify="space-between" align="middle">
                <Col>
                  <Title level={5} style={{ margin: 0 }}>AI Instructions</Title>
                </Col>
                <Col>
                  <Space>
                    <Button
                      icon={<PlusOutlined />}
                      size="small"
                      onClick={() => {
                        setEditingInstruction(null)
                        instructionForm.resetFields()
                        setShowInstructionModal(true)
                      }}
                    >
                      Add
                    </Button>
                    <Button
                      icon={<ReloadOutlined />}
                      size="small"
                      onClick={fetchInstructions}
                      loading={loadingInstructions}
                    >
                      Refresh
                    </Button>
                  </Space>
                </Col>
              </Row>

              <Select
                style={{ width: '100%' }}
                placeholder="Select an instruction"
                value={selectedInstructionId}
                onChange={setSelectedInstructionId}
                loading={loadingInstructions}
                size="large"
                notFoundContent={loadingInstructions ? <Spin size="small" /> : 'No instructions found'}
              >
                {instructions.map(inst => (
                  <Option key={inst.id} value={inst.id}>
                    <Row justify="space-between" align="middle">
                      <Col>{inst.name}</Col>
                      <Col>
                        <Space size="small">
                          <Button
                            icon={<EditOutlined />}
                            size="small"
                            type="link"
                            onClick={(e) => {
                              e.stopPropagation()
                              setEditingInstruction(inst)
                              instructionForm.setFieldsValue(inst)
                              setShowInstructionModal(true)
                            }}
                          />
                          <Popconfirm
                            title="Delete this instruction?"
                            onConfirm={(e) => {
                              e?.stopPropagation()
                              deleteInstruction(inst.id)
                            }}
                            onCancel={(e) => e?.stopPropagation()}
                          >
                            <Button
                              icon={<DeleteOutlined />}
                              size="small"
                              type="link"
                              danger
                              onClick={(e) => e.stopPropagation()}
                            />
                          </Popconfirm>
                        </Space>
                      </Col>
                    </Row>
                  </Option>
                ))}
              </Select>

              {selectedInstructionId && (
                <Card size="small" className="ai-testing-inline-preview">
                  <div
                    style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}
                    dangerouslySetInnerHTML={{
                      __html: sanitizeRichTextHtml((instructions.find(i => i.id === selectedInstructionId)?.instruction || '')
                        .replace(/\\n/g, '<br>'))
                    }}
                  />
                </Card>
              )}
            </Space>
          </Col>

          {/* Text Selection */}
          <Col span={12}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Row justify="space-between" align="middle">
                <Col>
                  <Title level={5} style={{ margin: 0 }}>
                    Texts to Translate
                    {selectedTextIds.length > 0 && (
                      <Tag color="blue" style={{ marginLeft: 8 }}>
                        {selectedTextIds.length} selected
                      </Tag>
                    )}
                  </Title>
                </Col>
                <Col>
                  <Space>
                    <Button
                      size="small"
                      onClick={() => setSelectedTextIds(testTexts.map(t => t.id))}
                    >
                      Select All
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setSelectedTextIds([])}
                    >
                      Clear
                    </Button>
                    <Button
                      icon={<PlusOutlined />}
                      size="small"
                      onClick={() => {
                        setEditingText(null)
                        textForm.resetFields()
                        setShowTextModal(true)
                      }}
                    >
                      Add
                    </Button>
                  </Space>
                </Col>
              </Row>

              <div className="ai-testing-scroll-panel ai-testing-scroll-panel--compact">
                <Checkbox.Group
                  value={selectedTextIds}
                  onChange={(values) => setSelectedTextIds(values as string[])}
                  style={{ width: '100%' }}
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {testTexts.map(text => (
                      <Checkbox
                        key={text.id}
                        value={text.id}
                        style={{ width: '100%' }}
                      >
                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                          <span>
                            {text.name}
                            {text.category && (
                              <Tag style={{ marginLeft: 8, fontSize: '12px', padding: '0 4px' }}>
                                {text.category}
                              </Tag>
                            )}
                          </span>
                          <Space size="small">
                            <Button
                              icon={<EditOutlined />}
                              size="small"
                              type="link"
                              onClick={(e) => {
                                e.stopPropagation()
                                setEditingText(text)
                                textForm.setFieldsValue(text)
                                setShowTextModal(true)
                              }}
                            />
                            <Popconfirm
                              title="Delete this text?"
                              onConfirm={(e) => {
                                e?.stopPropagation()
                                deleteText(text.id)
                              }}
                              onCancel={(e) => e?.stopPropagation()}
                            >
                              <Button
                                icon={<DeleteOutlined />}
                                size="small"
                                type="link"
                                danger
                                onClick={(e) => e.stopPropagation()}
                              />
                            </Popconfirm>
                          </Space>
                        </Space>
                      </Checkbox>
                    ))}
                  </Space>
                </Checkbox.Group>
              </div>

              {selectedTextIds.length > 0 && (
                <Card size="small">
                  <Text type="secondary">
                    {selectedTextIds.length} text{selectedTextIds.length !== 1 ? 's' : ''} selected
                    {numRuns > 1 && ` × ${numRuns} runs each = ${selectedTextIds.length * numRuns} total tests`}
                  </Text>
                </Card>
              )}
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Manual Input Section */}
      <Card title="Manual Input (Alternative)" className="ai-testing-section-card">
        <Row gutter={[24, 24]}>
          <Col span={12}>
            <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
              <Title level={5} style={{ margin: 0 }}>Manual AI Instructions</Title>
              <Tooltip title="Load the current production translation prompt for the selected target language">
                <Button
                  size="small"
                  onClick={handleImportProductionRules}
                  loading={importingProductionRules}
                  disabled={!selectedLanguage}
                >
                  Import Production Rules
                </Button>
              </Tooltip>
            </Space>
            <TextArea
              rows={6}
              value={instruction.replace(/\\n/g, '\n')}
              onChange={e => setInstruction(e.target.value.replace(/\n/g, '\\n'))}
              placeholder="Enter detailed AI instructions..."
            />
          </Col>
          <Col span={12}>
            <Title level={5}>Manual Text to Translate</Title>
            <TextArea
              rows={6}
              value={textToTranslate}
              onChange={e => setTextToTranslate(e.target.value)}
              placeholder="Enter text for translation..."
            />
          </Col>
        </Row>
      </Card>

      {/* Model Selection */}
      <Card title="Model Selection" className="ai-testing-section-card">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Input
            placeholder="Search models..."
            allowClear
            value={modelSearchTerm}
            onChange={e => setModelSearchTerm(e.target.value)}
          />
          {loadingModelCatalog && (
            <Alert
              type="info"
              showIcon
              message="Loading model catalog..."
            />
          )}
          <div className="ai-testing-scroll-panel">
            <Checkbox.Group
              value={selectedModels}
              onChange={(vals: CheckboxValueType[]) => setSelectedModels(vals as string[])}
              style={{ width: '100%' }}
            >
              {filteredModelGroups.length > 0 ? (
                filteredModelGroups.map((group, idx) => (
                  <div
                    key={group.key}
                    style={{ marginBottom: idx === filteredModelGroups.length - 1 ? 0 : 16 }}
                  >
                    <Space size="small" align="center" style={{ marginBottom: 8 }}>
                      <Text strong>{group.label}</Text>
                      <Tag color="blue">{group.models.length}</Tag>
                    </Space>
                    <Row gutter={[16, 8]}>
                      {group.models.map(model => (
                        <Col key={model.id} xs={24} sm={12} md={8}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: 6,
                              minWidth: 0,
                            }}
                          >
                            <Checkbox value={model.id}>
                              <span style={{ whiteSpace: 'nowrap' }}>
                                {model.id}
                              </span>
                            </Checkbox>
                            {!model.isActive && <Tag style={{ marginInlineEnd: 0 }}>inactive</Tag>}
                            {model.testingOnly && (
                              <Tag color="gold" style={{ marginInlineEnd: 0 }}>
                                testing
                              </Tag>
                            )}
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                ))
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    trimmedModelSearch
                      ? `No models match "${trimmedModelSearch}"`
                      : 'No models available'
                  }
                />
              )}
            </Checkbox.Group>
          </div>
        </Space>
      </Card>

      {/* Evaluator Model Selection */}
      <Card title="Evaluator Models" className="ai-testing-section-card">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="Select 1 to 4 evaluators. These models score translation quality."
          />
          <Input
            placeholder="Search evaluator models..."
            allowClear
            value={evaluationModelSearchTerm}
            onChange={e => setEvaluationModelSearchTerm(e.target.value)}
          />
          <div className="ai-testing-scroll-panel">
            <Checkbox.Group
              value={selectedEvaluationModels}
              onChange={handleEvaluationModelSelectionChange}
              style={{ width: '100%' }}
            >
              {filteredEvaluationModelGroups.length > 0 ? (
                filteredEvaluationModelGroups.map((group, idx) => (
                  <div
                    key={`eval-${group.key}`}
                    style={{ marginBottom: idx === filteredEvaluationModelGroups.length - 1 ? 0 : 16 }}
                  >
                    <Space size="small" align="center" style={{ marginBottom: 8 }}>
                      <Text strong>{group.label}</Text>
                      <Tag color="blue">{group.models.length}</Tag>
                    </Space>
                    <Row gutter={[16, 8]}>
                      {group.models.map(model => (
                        <Col key={`eval-${model.id}`} xs={24} sm={12} md={8}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: 6,
                              minWidth: 0,
                            }}
                          >
                            <Checkbox value={model.id}>
                              <span style={{ whiteSpace: 'nowrap' }}>
                                {model.id}
                              </span>
                            </Checkbox>
                            {!model.isActive && <Tag style={{ marginInlineEnd: 0 }}>inactive</Tag>}
                            {model.testingOnly && (
                              <Tag color="gold" style={{ marginInlineEnd: 0 }}>
                                testing
                              </Tag>
                            )}
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                ))
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    trimmedEvaluationModelSearch
                      ? `No evaluator models match "${trimmedEvaluationModelSearch}"`
                      : 'No evaluator models available'
                  }
                />
              )}
            </Checkbox.Group>
          </div>
          <Text type={selectedEvaluationModels.length ? undefined : 'danger'}>
            Selected evaluators: {selectedEvaluationModels.length}/4
          </Text>
        </Space>
      </Card>

      {/* Action Buttons */}
      <Card className="ai-testing-section-card ai-testing-run-card">
        <Row justify="center" gutter={16}>
          <Col>
            <Button
              size="large"
              onClick={handleSubmit}
              loading={loading}
              disabled={
                !instruction ||
                !textToTranslate ||
                !selectedModels.length ||
                !selectedEvaluationModels.length
              }
            >
              Run Manual Test
            </Button>
          </Col>
          <Col>
            <Button
              type="primary"
              size="large"
              onClick={handleSubmitExtended}
              loading={loading}
              disabled={
                !selectedInstructionId ||
                selectedTextIds.length === 0 ||
                !selectedModels.length ||
                !selectedEvaluationModels.length ||
                !selectedLanguage
              }
            >
              Run Batch Test ({selectedTextIds.length} text{selectedTextIds.length !== 1 ? 's' : ''} × {numRuns} run{numRuns !== 1 ? 's' : ''})
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Distributed Batch Progress */}
      {showBatchProgress && activeBatchId && (
        <DistributedBatchProgress
          batchId={activeBatchId}
          onComplete={handleDistributedBatchComplete}
          autoStart={true}
        />
      )}


      {/* Results */}
      {multiRunResults && (
        <Alert
          message={`Viewing averaged results from ${multiRunResults.totalRuns} runs`}
          type="info"
          style={{ marginBottom: '16px' }}
          action={
            <Button size="small" onClick={() => setShowIndividualRunsModal(true)}>
              View Individual Runs
            </Button>
          }
        />
      )}

      {testErrors.length > 0 && (
        <Card title="Error Summary" style={{ marginBottom: '24px' }}>
          <Collapse bordered={false}>
            {testErrors.map((err, idx) => {
              const stageLabel = err.stage ? err.stage.charAt(0).toUpperCase() + err.stage.slice(1) : 'Error'
              const header = `${stageLabel} - ${err.model || 'Unknown model'}`
              return (
                <Panel header={header} key={`${idx}-${stageLabel}`}>
                  <Space direction="vertical" size="small">
                    {(err.text_name || err.text_id) && (
                      <Space size={4}>
                        {err.text_name && <Tag color="blue">Text: {err.text_name}</Tag>}
                        {err.text_id && <Tag color="geekblue">ID: {err.text_id}</Tag>}
                      </Space>
                    )}
                    {err.model && <Tag color="purple">Model: {err.model}</Tag>}
                    <Typography.Text type="danger">{err.message}</Typography.Text>
                    {err.details && (
                      <Typography.Paragraph style={{ marginBottom: 0 }}>
                        <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{err.details}</pre>
                      </Typography.Paragraph>
                    )}
                  </Space>
                </Panel>
              )
            })}
          </Collapse>
        </Card>
      )}

      {Object.keys(groupedResults).length > 0 ? (
        <div>
          <ResultsTabs
            groupedResults={groupedResults}
            translationResults={translationResults}
            overallScores={overallScores}
            costBreakdowns={costBreakdowns}
            EVAL_WEIGHTS={EVAL_WEIGHTS}
            isValidScore={isValidScore}
            getScoreColor={getScoreColor}
            parseEvaluationResult={parseEvaluationResult}
            selectedCompareModels={selectedCompareModels}
            onCompareCheckboxChange={onCompareCheckboxChange}
            onCompare={handleCompare}
            onOpenTranslation={openTranslationModal}
            onOpenSideBySide={openSideBySideModal}
            onOpenEmDashCompare={openEmDashCompare}
            onToggleDiscard={toggleResultDeletion}
          />

          {Object.keys(costBreakdowns).length > 0 && (
            <CostQualityAnalysis
              enhancedCostAnalysis={enhancedCostAnalysis}
              multiRunResults={multiRunResults}
              getScoreColor={getScoreColor}
            />
          )}
        </div>
      ) : (
        <Alert
          message="No Results"
          description="Configure your test and click 'Run Database Test' to see results."
          type="info"
          showIcon
          style={{ marginBottom: '24px' }}
        />
      )}

      {/* Modals */}
      <TranslationModal
        visible={showTranslationModal}
        content={translationToShow}
        onClose={() => setShowTranslationModal(false)}
      />

      <SideBySideModal
        visible={showSideBySideModal}
        originalHTML={textToTranslate}
        translationHTML={sideBySideTranslation}
        onClose={() => {
          setShowSideBySideModal(false)
          setSideBySideTranslation('')
        }}
      />

      <SideBySideModal
        visible={showEmDashCompareModal}
        originalHTML={emDashBeforeHTML}
        translationHTML={emDashAfterHTML}
        onClose={() => {
          setShowEmDashCompareModal(false)
          setEmDashBeforeHTML('')
          setEmDashAfterHTML('')
        }}
      />

      <CompareModal
        visible={showCompareModal}
        result={compareResult}
        onClose={() => {
          setShowCompareModal(false)
          setCompareResult(null)
          setSelectedCompareModels([])
        }}
      />

      <SavedResultsModal
        visible={showSavedModal}
        loading={loadingDbResults}
        dbResults={dbResults}
        onClose={() => setShowSavedModal(false)}
        onLoad={handleLoadSavedResult}
        onDelete={handleDeleteSavedResult}
        totalCount={savedTotalCount}
        pageSize={savedPageSize}
        currentPage={savedPage}
        onPageChange={(page, size) => fetchDbResults(page, size || savedPageSize)}
      />

      <IndividualRunsModal
        visible={showIndividualRunsModal}
        onClose={() => setShowIndividualRunsModal(false)}
        multiRunResults={multiRunResults}
        parseEvaluationResult={parseEvaluationResult}
        hasAllInvalidScores={hasAllInvalidScores}
        isValidScore={isValidScore}
        getScoreColor={getScoreColor}
        calculateCost={(model: string, tokenInfo: TokenInfo) => calculateCost(model, tokenInfo, modelCostRates)}
        onShowTranslation={(content: string) => {
          setTranslationToShow(content)
          setShowTranslationModal(true)
        }}
      />

      {/* Instruction Management Modal */}
      <Modal
        title={`${editingInstruction ? 'Edit' : 'Add'} Instruction`}
        visible={showInstructionModal}
        onCancel={() => {
          setShowInstructionModal(false)
          setEditingInstruction(null)
          instructionForm.resetFields()
        }}
        footer={null}
        width={700}
      >
        <Form form={instructionForm} layout="vertical" onFinish={handleInstructionSubmit}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Please enter a name' }]}>
            <Input placeholder="e.g., Standard Translation Instructions" />
          </Form.Item>
          <Form.Item name="instruction" label="Instruction" rules={[{ required: true, message: 'Please enter instructions' }]}>
            <TextArea rows={8} placeholder="Enter AI instructions..." />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingInstruction ? 'Update' : 'Create'}
              </Button>
              <Button onClick={() => {
                setShowInstructionModal(false)
                setEditingInstruction(null)
                instructionForm.resetFields()
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Text Management Modal */}
      <Modal
        title={`${editingText ? 'Edit' : 'Add'} Text`}
        visible={showTextModal}
        onCancel={() => {
          setShowTextModal(false)
          setEditingText(null)
          textForm.resetFields()
        }}
        footer={null}
        width={700}
      >
        <Form form={textForm} layout="vertical" onFinish={handleTextSubmit}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Please enter a name' }]}>
            <Input placeholder="e.g., Chapter 1 Opening" />
          </Form.Item>
          <Form.Item name="category" label="Category (Optional)">
            <Input placeholder="e.g., Fiction, Technical, Dialog" />
          </Form.Item>
          <Form.Item name="text" label="Text" rules={[{ required: true, message: 'Please enter text' }]}>
            <TextArea rows={10} placeholder="Enter text to translate..." />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingText ? 'Update' : 'Create'}
              </Button>
              <Button onClick={() => {
                setShowTextModal(false)
                setEditingText(null)
                textForm.resetFields()
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <AggregateResultsModal
        visible={showAggregateModal}
        onClose={() => setShowAggregateModal(false)}
        dbResults={dbResults}
        languageTotalCount={aggregateTotalCount}
        selectedLanguage={selectedLanguageForAggregate}
        selectedInstructionId={selectedInstructionForAggregate}
        selectedTestName={selectedTestNameForAggregate}
        selectedBatchId={selectedBatchForAggregate}
        setSelectedLanguage={handleAggregateLanguageChange}
        setSelectedInstructionId={handleAggregateInstructionChange}
        setSelectedTestName={handleAggregateTestNameChange}
        setSelectedBatchId={handleAggregateBatchChange}
        getAvailableInstructionsForLanguage={getAvailableInstructionsForLanguage}
        getAvailableTestNamesForLanguage={getAvailableTestNamesForLanguage}
        getAvailableBatchesForLanguage={getAvailableBatchesForLanguage}
        getAggregatedResults={getAggregatedResults}
        getModelRankings={getModelRankings}
        downloadAggregateResults={downloadAggregateResults}
        downloadAllAggregateResults={downloadAllAggregateResults}
        loadAggregateResults={loadAggregateDataForSelection}
        hasLoadedData={hasLoadedAggregateData}
        filtersDirty={aggregateFiltersDirty}
        getScoreColor={getScoreColor}
        getEvaluationCostAggregates={getEvaluationCostAggregates}
        loadingData={loadingAggregateData}
        loadingInstructionOptions={loadingAggregateInstructionOptions}
        loadingTestNameOptions={loadingAggregateTestNameOptions}
        loadingBatchOptions={loadingAggregateBatchOptions}
      />

      {/* Active Batches Modal */}
      <Modal
        title="Active Batch Jobs"
        visible={showActiveBatches}
        onCancel={() => setShowActiveBatches(false)}
        footer={null}
        width={800}
      >
        <ActiveBatchesList
          onSelectBatch={(batchId) => {
            setActiveBatchId(batchId)
            setShowBatchProgress(true)
            setShowActiveBatches(false)
          }}
        />
      </Modal>
    </div>
  )
}

export default AIExperimentTestingTab
